import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiUrl, apiUnreachableMessage } from '../config/api';
import { DEFAULT_SUPER_ADMIN_COMPANY, findCompanyIdByName } from '../config/defaults';
import { CompanySelectField } from './CompanySelectField';
import {
  AddFieldButton,
  AdminDataTable,
  DeleteButton,
  EditButton,
  SiteInfoButton,
} from './AdminActionButtons';
import { ListPagination } from './ListPagination';
import { panelAddButtonClass } from './LogoutButton';
import CertificateUploadModal from './CertificateUploadModal';
import {
  CertificatesNotifyRow,
  EmployeeCertificateFields,
  uploadClientCertificate,
} from './EmployeeCertificateFields';
import { PasswordInput } from './PasswordInput';
import { getPreferredCompanyId, setLastCreatedCompanyId } from '../utils/companySelection';
import { useAdminCompanies } from '../utils/useAdminCompanies';
import { usePagination } from '../hooks/usePagination';
import { getStoredUser } from '../utils/auth';
import {
  CLIENT_ROLE_OPTIONS,
  COMPANY_ADMIN_ROLE_OPTIONS,
  PLATFORM_ROLE_OPTIONS,
  ROLE_LABELS,
  SAFETY_SUPERVISOR_ROLE_OPTIONS,
  CLIENT_ROLE_STATS_OPTIONS,
  isSafetySupervisorRole,
  isSuperAdmin,
  isSuperUser,
  canAssignSites,
  canAssignWorkers,
  roleLabel,
} from '../utils/roles';

const inputClass =
  'w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600';
const notesFieldClass =
  'px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 w-full resize-y min-h-[4.5rem]';
const labelClass = 'block text-sm font-medium text-gray-300 mb-2';

function adminHeadersJson() {
  const user = getStoredUser();
  return {
    'Content-Type': 'application/json',
    'X-User-Email': user?.email || '',
  };
}

function adminHeadersMultipart() {
  const user = getStoredUser();
  return {
    'X-User-Email': user?.email || '',
  };
}

async function adminDelete(resource, id) {
  const res = await fetch(apiUrl(`/api/admin/${resource}/${id}`), {
    method: 'DELETE',
    headers: adminHeadersJson(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data.detail || 'Delete failed' };
  }
  return { ok: true, message: data.message || 'Moved to archive' };
}

async function adminRestore(resource, id) {
  const res = await fetch(apiUrl(`/api/admin/${resource}/${id}/restore`), {
    method: 'POST',
    headers: adminHeadersJson(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data.detail || 'Restore failed' };
  }
  return { ok: true, message: data.message || 'Restored successfully' };
}

function formatArchivedAt(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const archiveToggleClass = (active) =>
  `min-h-11 px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors lg:min-h-0 ${
    active
      ? 'bg-gray-200 text-black border-gray-200'
      : 'bg-gray-900/60 text-gray-300 border-gray-700 hover:border-gray-500 hover:text-white'
  }`;

function Message({ error, success }) {
  if (error) {
    return (
      <div className="text-red-400 text-sm bg-red-400/10 py-2 px-3 rounded mb-4">{error}</div>
    );
  }
  if (success) {
    return (
      <div className="text-green-400 text-xs bg-green-400/10 py-1.5 px-3 rounded mb-3 inline-block">
        {success}
      </div>
    );
  }
  return null;
}

function ListShell({ title, description, onBack, children, wide, headerAction, leadingAction, titleAside, titleAction }) {
  const hasTopBar = onBack || titleAction;

  return (
    <div className={wide ? 'w-full max-w-none min-w-0' : 'w-full max-w-md min-w-0'}>
      {hasTopBar && (
        <div className="flex flex-col items-stretch gap-2 mb-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:mb-1.5">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
            >
              ← Back to dashboard
            </button>
          ) : (
            <span />
          )}
          {titleAction ? (
            <div className="w-full lg:w-auto lg:ml-auto lg:shrink-0">{titleAction}</div>
          ) : null}
        </div>
      )}
      {leadingAction}
      <div
        className={`flex flex-col items-stretch gap-2 mb-3 lg:flex-row lg:flex-wrap lg:items-center ${
          titleAside ? 'lg:justify-between' : ''
        }`}
      >
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-white leading-tight">{title}</h2>
          {description ? (
            <p className="text-gray-400 text-xs mt-0.5">{description}</p>
          ) : null}
        </div>
        {titleAside ? (
          <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto lg:justify-end lg:ml-auto lg:shrink-0">
            {titleAside}
          </div>
        ) : null}
      </div>
      {headerAction}
      {children}
    </div>
  );
}

function sortSitesNewestFirst(sites) {
  return [...(sites || [])].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (bTime !== aTime) return bTime - aTime;
    return Number(b.id || 0) - Number(a.id || 0);
  });
}

const assignmentSelectClass =
  'w-[95%] min-w-0 max-w-[22rem] px-2 py-2 bg-black border border-gray-700 rounded text-sm text-gray-200 focus:outline-none focus:ring-1 focus:ring-amber-300/50 disabled:opacity-60 lg:w-[65%] lg:min-w-[11rem] lg:max-w-[18rem] lg:py-1.5';

/** Shared column shell so selects stay aligned across role columns. */
function AssignmentColumn({ label, chips, control }) {
  return (
    <div className="min-w-0 flex flex-col h-full">
      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1 shrink-0">
        {label}
      </label>
      <div className="shrink-0">{control}</div>
      <div className="mt-1.5 min-h-[1.75rem] shrink-0">{chips}</div>
    </div>
  );
}

const ASSIGNMENT_NAME_PREVIEW = 2;

function AssignmentPersonChips({ people, disabled = false, onRemove, title = 'Assigned people' }) {
  const [showAll, setShowAll] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState(null);
  const items = Array.isArray(people) ? people : [];

  useEffect(() => {
    if (pendingRemoveId == null) return undefined;
    const timer = window.setTimeout(() => setPendingRemoveId(null), 3000);
    return () => window.clearTimeout(timer);
  }, [pendingRemoveId]);

  if (!items.length) {
    return null;
  }

  const preview = items.slice(0, ASSIGNMENT_NAME_PREVIEW);
  const hasMore = items.length > ASSIGNMENT_NAME_PREVIEW;

  const handleRemoveClick = (personId) => {
    if (disabled) return;
    if (String(pendingRemoveId) === String(personId)) {
      setPendingRemoveId(null);
      onRemove?.(personId);
      return;
    }
    setPendingRemoveId(personId);
  };

  const renderChip = (person) => {
    const confirming = String(pendingRemoveId) === String(person.id);
    const label = person.name || `User #${person.id}`;
    return (
      <span
        key={person.id}
        className={`relative inline-flex items-center gap-1 max-w-[9rem] shrink-0 rounded-md border px-2 py-0.5 text-xs overflow-hidden ${
          confirming
            ? 'border-red-700 bg-red-700 text-red-50'
            : 'border-gray-700 bg-gray-900 text-gray-200'
        }`}
      >
        <span className={`truncate ${confirming ? 'invisible' : ''}`}>{label}</span>
        <button
          type="button"
          disabled={disabled || confirming}
          onClick={() => handleRemoveClick(person.id)}
          className={`shrink-0 leading-none disabled:opacity-40 ${
            confirming ? 'invisible' : 'text-gray-500 hover:text-white'
          }`}
          title={`Remove ${label}`}
          aria-label={`Remove ${label}`}
          tabIndex={confirming ? -1 : 0}
        >
          ×
        </button>
        {confirming ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleRemoveClick(person.id)}
            className="absolute inset-0 flex items-center justify-center bg-red-700 text-sm font-semibold text-red-50 hover:bg-red-600 disabled:opacity-40"
            title="Click again to remove"
            aria-label={`Confirm remove ${label}`}
          >
            sure?
          </button>
        ) : null}
      </span>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 items-center">
        {preview.map(renderChip)}
        {hasMore ? (
          <button
            type="button"
            onClick={() => {
              setPendingRemoveId(null);
              setShowAll(true);
            }}
            className="text-[11px] text-amber-200/80 hover:text-amber-200 underline shrink-0"
          >
            View all {items.length}
          </button>
        ) : null}
      </div>
      {showAll && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={() => {
            setPendingRemoveId(null);
            setShowAll(false);
          }}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-lg border border-gray-700 bg-gray-950 p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h4 className="text-sm font-semibold text-white">{title}</h4>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {items.length} {items.length === 1 ? 'person' : 'people'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPendingRemoveId(null);
                  setShowAll(false);
                }}
                className="text-xs text-gray-400 hover:text-white"
              >
                Close
              </button>
            </div>
            <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {items.map((person) => {
                const confirming = String(pendingRemoveId) === String(person.id);
                const label = person.name || `User #${person.id}`;
                return (
                  <li
                    key={person.id}
                    className={`relative flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 overflow-hidden ${
                      confirming
                        ? 'border-red-700 bg-red-700'
                        : 'border-gray-800 bg-gray-900/60'
                    }`}
                  >
                    <span
                      className={`text-xs truncate min-w-0 ${
                        confirming ? 'invisible' : 'text-gray-200'
                      }`}
                    >
                      {label}
                    </span>
                    <button
                      type="button"
                      disabled={disabled || confirming}
                      onClick={() => handleRemoveClick(person.id)}
                      className={`shrink-0 text-base leading-none px-1 disabled:opacity-40 ${
                        confirming ? 'invisible' : 'text-gray-500 hover:text-white'
                      }`}
                      title={`Remove ${label}`}
                      aria-label={`Remove ${label}`}
                      tabIndex={confirming ? -1 : 0}
                    >
                      ×
                    </button>
                    {confirming ? (
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => handleRemoveClick(person.id)}
                        className="absolute inset-0 flex items-center justify-center bg-red-700 text-sm font-semibold text-red-50 hover:bg-red-600 disabled:opacity-40"
                        title="Click again to remove"
                        aria-label={`Confirm remove ${label}`}
                      >
                        sure?
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/** Selected people as chips + dropdown to add more. */
function PersonAssignmentChips({
  label,
  selectedPeople,
  options,
  disabled = false,
  onChange,
  ariaLabel,
}) {
  const selectedIds = (selectedPeople || []).map((p) => Number(p.id));
  const selectedSet = new Set(selectedIds.map(String));
  const available = (options || []).filter((person) => !selectedSet.has(String(person.id)));

  const removePerson = (userId) => {
    onChange(selectedIds.filter((id) => id !== Number(userId)));
  };

  const addPerson = (rawValue) => {
    if (!rawValue) return;
    const nextId = Number(rawValue);
    if (!nextId || selectedSet.has(String(nextId))) return;
    onChange([...selectedIds, nextId]);
  };

  return (
    <AssignmentColumn
      label={label}
      chips={
        <AssignmentPersonChips
          people={selectedPeople}
          disabled={disabled}
          onRemove={removePerson}
          title={label}
        />
      }
      control={
        <select
          value=""
          onChange={(e) => {
            addPerson(e.target.value);
            e.target.value = '';
          }}
          disabled={disabled || !available.length}
          className={assignmentSelectClass}
          aria-label={ariaLabel}
        >
          <option value="">{available.length ? 'Add…' : 'No more people'}</option>
          {available.map((person) => (
            <option key={person.id} value={String(person.id)}>
              {person.name}
              {person.role === 'user' ? ' (User/Worker)' : ''}
            </option>
          ))}
        </select>
      }
    />
  );
}

/** Multi-select sites panel with checkboxes; × hides the panel. */
function SiteMultiSelectDropdown({
  sites,
  selectedIds,
  onChange,
  open,
  onOpenChange,
  loading = false,
  disabled = false,
  label = 'Assign sites',
  required = true,
}) {
  if (!open) {
    return (
      <div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onOpenChange(true)}
          className="text-xs text-amber-200/80 hover:text-amber-200 underline disabled:opacity-40 disabled:no-underline"
        >
          Choose sites{required ? ' *' : ''}
        </button>
        {required ? (
          <p className="text-[11px] text-gray-500 mt-1">Site assignment is required.</p>
        ) : null}
      </div>
    );
  }

  const selectedSet = new Set((selectedIds || []).map(String));
  const toggle = (siteId) => {
    const id = String(siteId);
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next).map((v) => Number(v)));
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-950/80 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-800">
        <span className="text-xs font-medium text-gray-300">
          {label}
          {required ? <span className="text-amber-200/90"> *</span> : null}
        </span>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:text-white hover:bg-gray-800 text-sm"
          title="Hide"
          aria-label="Hide sites selection"
        >
          ×
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto px-3 py-2 space-y-1.5">
        {loading ? (
          <p className="text-xs text-gray-500 py-1">Loading sites…</p>
        ) : !sites.length ? (
          <p className="text-xs text-gray-500 py-1">No sites for this company.</p>
        ) : (
          sites.map((site) => {
            const checked = selectedSet.has(String(site.id));
            return (
              <label
                key={site.id}
                className="flex items-start gap-2 text-sm text-gray-200 cursor-pointer hover:text-white"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(site.id)}
                  className="mt-0.5 rounded border-gray-600 bg-black text-amber-400 focus:ring-amber-400/40"
                />
                <span className="min-w-0">
                  <span className="block truncate">{site.name}</span>
                  {site.address ? (
                    <span className="block text-[11px] text-gray-500 truncate">{site.address}</span>
                  ) : null}
                </span>
              </label>
            );
          })
        )}
      </div>
      <p className="px-3 py-1.5 border-t border-gray-800 text-[11px] text-gray-500">
        {selectedIds?.length
          ? `${selectedIds.length} site${selectedIds.length === 1 ? '' : 's'} selected`
          : 'Select at least one site'}
      </p>
    </div>
  );
}

const ROLE_OPTIONS = PLATFORM_ROLE_OPTIONS;

const ADMIN_VISIBLE_ROLE_LABELS = {
  user: ROLE_LABELS.user,
  admin: ROLE_LABELS.admin,
  super_visor: ROLE_LABELS.super_visor,
  assistant_super_visor: ROLE_LABELS.assistant_super_visor,
};

function canDeleteUserRow(row) {
  const actor = getStoredUser();
  if (!actor) return false;
  if (row.email === actor.email) return false;
  if (actor.role === 'super_admin') return true;
  if (actor.role === 'super_user') return isSafetySupervisorRole(row.role);
  if (isSafetySupervisorRole(row.role)) return true;
  return row.role === 'user';
}

function canEditUserRow(row) {
  const actor = getStoredUser();
  if (!actor) return false;
  if (actor.role === 'super_admin' || actor.role === 'super_user') return true;
  if (row.email === actor.email) return true;
  if (isSafetySupervisorRole(row.role)) return true;
  return row.role === 'user';
}

function roleOptionsForActor(actor, { internalOnly = false, currentRole = null } = {}) {
  let options;
  if (!actor) {
    options = COMPANY_ADMIN_ROLE_OPTIONS;
  } else if (isSuperAdmin(actor.role)) {
    options = internalOnly ? ROLE_OPTIONS : ROLE_OPTIONS;
  } else if (isSuperUser(actor.role)) {
    options = SAFETY_SUPERVISOR_ROLE_OPTIONS;
  } else {
    options = COMPANY_ADMIN_ROLE_OPTIONS;
  }
  // Keep current Assistant Supervisor visible when editing an existing account
  // (role is no longer offered for new users; assignment is managed on Sites).
  if (
    currentRole === 'assistant_super_visor' &&
    !options.some((option) => option.value === currentRole)
  ) {
    return [
      ...options,
      { value: 'assistant_super_visor', label: ROLE_LABELS.assistant_super_visor },
    ];
  }
  return options;
}

function parseStoredTimestamp(value) {
  if (!value) return null;
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)) {
    return new Date(text.replace(' ', 'T') + 'Z');
  }
  return new Date(text);
}

function formatNoteTimestamp(value) {
  const date = parseStoredTimestamp(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function SiteCardHeader({ row, hasActions, onEdit, onDelete, deleting }) {
  const [showInfo, setShowInfo] = useState(false);
  const addressLabel =
    row.company_name && row.address
      ? `${row.company_name} · ${row.address}`
      : row.company_name || row.address || '—';

  return (
    <>
      <div className="relative flex items-center justify-between gap-2 min-h-[2.25rem] px-0 -mt-1 mb-2 lg:justify-center lg:px-24">
        <h3 className="min-w-0 flex-1 truncate text-left lg:flex-none lg:max-w-[min(100%,28rem)] lg:text-center">
          <span className="inline-flex max-w-full items-center rounded-md border border-white/10 bg-gradient-to-b from-white/[0.07] to-transparent px-3.5 py-1.5 text-[15px] font-semibold tracking-tight text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
            <span className="min-w-0 truncate">{row.name || '—'}</span>
          </span>
        </h3>
        <div className="flex items-center justify-end gap-1.5 shrink-0 lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2">
          <SiteInfoButton onClick={() => setShowInfo(true)} />
          {hasActions && (
            <>
              {onEdit && <EditButton onClick={() => onEdit(row)} />}
              {onDelete && <DeleteButton onClick={() => onDelete(row)} deleting={deleting} />}
            </>
          )}
        </div>
      </div>
      {showInfo && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-black/70 p-2 sm:p-4"
          onClick={() => setShowInfo(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-sm max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-lg border border-gray-700 bg-gray-950 p-4 shadow-xl lg:max-h-none lg:overflow-visible"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Site info"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <h4 className="text-sm font-semibold text-white">Site info</h4>
              <button
                type="button"
                onClick={() => setShowInfo(false)}
                className="text-xs text-gray-400 hover:text-white"
              >
                Close
              </button>
            </div>
            <div className="space-y-2 text-xs text-gray-400">
              <div className="break-words">
                Site name <span className="text-gray-600">→</span>{' '}
                <span className="text-white font-medium">{row.name || '—'}</span>
              </div>
              <div className="break-words">
                Site address <span className="text-gray-600">→</span> {addressLabel}
              </div>
              <div className="break-words">
                Created at <span className="text-gray-600">→</span>{' '}
                <span className="text-white font-medium">
                  {row.created_at ? formatNoteTimestamp(row.created_at) : '—'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SiteAssignmentTimestampHistory({ sections }) {
  const [showAll, setShowAll] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const groups = (sections || []).filter(Boolean);
  const totalEntries = groups.reduce(
    (sum, section) => sum + (Array.isArray(section.items) ? section.items.length : 0),
    0
  );
  const activeSection = groups.find((section) => section.key === activeKey) || null;
  const activeItems = Array.isArray(activeSection?.items) ? activeSection.items : [];

  const closeModal = () => {
    setShowAll(false);
    setActiveKey(null);
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => {
          setActiveKey(null);
          setShowAll(true);
        }}
        className="text-[11px] text-amber-200/80 hover:text-amber-200 underline"
      >
        View time stamp history
      </button>
      {showAll && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-950 p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Timestamp History"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                {activeSection ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setActiveKey(null)}
                      className="text-[11px] text-amber-200/80 hover:text-amber-200 underline mb-1"
                    >
                      ← All categories
                    </button>
                    <h4 className="text-sm font-semibold text-white">{activeSection.label}</h4>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Latest first · {activeItems.length}{' '}
                      {activeItems.length === 1 ? 'entry' : 'entries'}
                    </p>
                  </>
                ) : (
                  <>
                    <h4 className="text-sm font-semibold text-white">Timestamp History</h4>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Choose a category
                      {totalEntries
                        ? ` · ${totalEntries} ${totalEntries === 1 ? 'entry' : 'entries'} total`
                        : ''}
                    </p>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="text-xs text-gray-400 hover:text-white shrink-0"
              >
                Close
              </button>
            </div>

            {!activeSection ? (
              <ul className="space-y-2">
                {groups.map((section) => {
                  const count = Array.isArray(section.items) ? section.items.length : 0;
                  return (
                    <li key={section.key}>
                      <button
                        type="button"
                        onClick={() => setActiveKey(section.key)}
                        className="w-full flex items-center justify-between gap-3 rounded-md border border-gray-800 bg-gray-900/50 px-3 py-2.5 text-left hover:border-amber-300/40 hover:bg-gray-900 transition-colors"
                      >
                        <span className="text-sm text-gray-200">{section.label}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-[11px] text-gray-500">
                            {count} {count === 1 ? 'entry' : 'entries'}
                          </span>
                          <span className="text-gray-300 text-base leading-none" aria-hidden>
                            ›
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {activeItems.length ? (
                  activeItems.map((entry, index) => {
                    const action = String(entry.action || 'assigned').toLowerCase();
                    const actionLabel = action === 'removed' ? 'removed' : 'added';
                    const byName = entry.assigned_by_name || '';
                    return (
                      <li
                        key={`${activeSection.key}-${entry.user_id || 'u'}-${entry.assigned_at || index}-${action}-${index}`}
                        className="text-xs border-b border-gray-800/80 pb-1.5 last:border-0 text-white"
                      >
                        <span className="text-gray-600 mr-1.5">{index + 1}.</span>
                        <span className="text-white">{entry.name || 'Unknown'}</span>{' '}
                        <span
                          className={
                            action === 'removed' ? 'text-rose-300/90' : 'text-emerald-300/90'
                          }
                        >
                          ({actionLabel})
                        </span>
                        <span className="block pl-5 text-[11px] text-gray-500 mt-0.5">
                          {entry.assigned_at ? formatNoteTimestamp(entry.assigned_at) : '—'}
                          {byName ? ` · by ${byName}` : ''}
                        </span>
                      </li>
                    );
                  })
                ) : (
                  <li className="text-[11px] text-gray-600 py-2">No history for this category.</li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SitesAssignmentList({
  rows,
  emptyText,
  companyScopeId,
  showAssignSupervisor,
  showAssignProjectManager,
  showAssignAssistant,
  showAssignWorker,
  readOnly,
  assigningId,
  supervisorOptions,
  projectManagerOptions,
  assistantOptions,
  workerOptions,
  onAssignSupervisor,
  onAssignProjectManager,
  onAssignAssistantSupervisor,
  onAssignWorker,
  onEdit,
  onDelete,
  deletingId,
}) {
  if (!rows?.length) {
    return (
      <p className="text-gray-500 text-sm py-6 text-center border border-gray-800 rounded-lg">
        {emptyText}
      </p>
    );
  }

  const hasActions = !readOnly && (onEdit || onDelete);
  const showAssignments =
    showAssignSupervisor ||
    showAssignProjectManager ||
    showAssignAssistant ||
    showAssignWorker;
  const rowGridClass = 'grid grid-cols-1 lg:grid-cols-2 gap-3 items-start';

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const assistantValue =
          row.assigned_assistant_user_id != null && row.assigned_assistant_user_id !== ''
            ? String(row.assigned_assistant_user_id)
            : '';
        const projectManagerValue =
          row.assigned_project_manager_user_id != null &&
          row.assigned_project_manager_user_id !== ''
            ? String(row.assigned_project_manager_user_id)
            : '';
        const assignedSupervisors = Array.isArray(row.assigned_supervisors)
          ? row.assigned_supervisors
          : row.assigned_supervisor_user_id
            ? [
                {
                  id: row.assigned_supervisor_user_id,
                  name: row.assigned_supervisor_name || 'Assigned supervisor',
                },
              ]
            : [];
        const assignedWorkers = Array.isArray(row.assigned_workers)
          ? row.assigned_workers
          : row.assigned_to_user_id
            ? [
                {
                  id: row.assigned_to_user_id,
                  name: row.assigned_to_name || 'Assigned worker',
                  role: 'user',
                },
              ]
            : [];
        const hasSupervisor = assignedSupervisors.length > 0 || !!row.assigned_supervisor_user_id;
        const supervisorHistory = Array.isArray(row.supervisor_assignment_history)
          ? row.supervisor_assignment_history
          : [];
        const projectManagerHistory = Array.isArray(row.project_manager_assignment_history)
          ? row.project_manager_assignment_history
          : [];
        const assistantHistory = Array.isArray(row.assistant_assignment_history)
          ? row.assistant_assignment_history
          : [];
        const workerHistory = Array.isArray(row.worker_assignment_history)
          ? row.worker_assignment_history
          : [];

        const supervisorColumn = showAssignSupervisor ? (
          <PersonAssignmentChips
            label="Assign Supervisor(s)"
            selectedPeople={assignedSupervisors}
            options={supervisorOptions(row)}
            disabled={assigningId === row.id}
            onChange={(ids) => onAssignSupervisor(row, ids)}
            ariaLabel={`Assign supervisors for ${row.name}`}
          />
        ) : null;

        const projectManagerColumn = showAssignProjectManager ? (
          <AssignmentColumn
            label="Assign Project Manager"
            chips={
              projectManagerValue ? (
                <AssignmentPersonChips
                  people={[
                    {
                      id: row.assigned_project_manager_user_id,
                      name: row.assigned_project_manager_name || 'Assigned project manager',
                    },
                  ]}
                  disabled={assigningId === row.id}
                  onRemove={() => onAssignProjectManager(row, '')}
                />
              ) : null
            }
            control={
              <select
                value=""
                onChange={(e) => {
                  const raw = e.target.value;
                  e.target.value = '';
                  if (!raw) return;
                  onAssignProjectManager(row, raw === '__clear__' ? '' : raw);
                }}
                disabled={assigningId === row.id}
                className={assignmentSelectClass}
                aria-label={`Assign project manager for ${row.name}`}
              >
                <option value="">
                  {projectManagerValue ? 'Change…' : 'Assign…'}
                </option>
                {projectManagerValue ? <option value="__clear__">Unassigned</option> : null}
                {projectManagerOptions(row)
                  .filter(
                    (person) =>
                      String(person.id) !== String(row.assigned_project_manager_user_id || '')
                  )
                  .map((person) => (
                    <option key={person.id} value={String(person.id)}>
                      {person.name}
                    </option>
                  ))}
              </select>
            }
          />
        ) : null;

        const assistantColumn = showAssignAssistant ? (
          <AssignmentColumn
            label="Assign Assistant Supervisor"
            chips={
              assistantValue ? (
                <AssignmentPersonChips
                  people={[
                    {
                      id: row.assigned_assistant_user_id,
                      name: row.assigned_assistant_name || 'Assigned assistant',
                    },
                  ]}
                  disabled={assigningId === row.id || !hasSupervisor}
                  onRemove={() => onAssignAssistantSupervisor(row, '')}
                />
              ) : null
            }
            control={
              <select
                value=""
                onChange={(e) => {
                  const raw = e.target.value;
                  e.target.value = '';
                  if (!raw) return;
                  onAssignAssistantSupervisor(row, raw === '__clear__' ? '' : raw);
                }}
                disabled={assigningId === row.id || !hasSupervisor}
                className={assignmentSelectClass}
                aria-label={`Assign assistant supervisor for ${row.name}`}
                title={!hasSupervisor ? 'Assign a supervisor first' : undefined}
              >
                <option value="">{assistantValue ? 'Change…' : 'Assign…'}</option>
                {assistantValue ? <option value="__clear__">Unassigned</option> : null}
                {assistantOptions(row)
                  .filter(
                    (person) =>
                      String(person.id) !== String(row.assigned_assistant_user_id || '')
                  )
                  .map((person) => (
                    <option key={person.id} value={String(person.id)}>
                      {person.name}
                      {person.role === 'user' ? ' (User/Worker)' : ''}
                    </option>
                  ))}
              </select>
            }
          />
        ) : null;

        const workerColumn = showAssignWorker ? (
          <PersonAssignmentChips
            label="Assign Worker(s)"
            selectedPeople={assignedWorkers}
            options={workerOptions(row)}
            disabled={assigningId === row.id}
            onChange={(ids) => onAssignWorker(row, ids)}
            ariaLabel={`Assign workers for ${row.name}`}
          />
        ) : null;

        const historySections = [
          showAssignSupervisor
            ? { key: 'supervisor', label: 'Supervisor(s)', items: supervisorHistory }
            : null,
          showAssignProjectManager
            ? {
                key: 'project_manager',
                label: 'Project Manager',
                items: projectManagerHistory,
              }
            : null,
          showAssignAssistant
            ? { key: 'assistant', label: 'Assistant Supervisor', items: assistantHistory }
            : null,
          showAssignWorker ? { key: 'worker', label: 'Worker(s)', items: workerHistory } : null,
        ].filter(Boolean);

        return (
          <article
            key={row.id}
            className="border border-gray-800 rounded-lg px-3 pt-2 pb-3 bg-gray-950/40 hover:bg-gray-900/40 transition-colors sm:px-4"
          >
            <SiteCardHeader
              row={row}
              hasActions={hasActions}
              onEdit={onEdit}
              onDelete={onDelete}
              deleting={deletingId === row.id}
            />

            {showAssignments && (
              <div className="mt-5 pt-1 space-y-3">
                {(supervisorColumn || projectManagerColumn) && (
                  <div className={rowGridClass}>
                    {supervisorColumn}
                    {projectManagerColumn}
                  </div>
                )}
                {(assistantColumn || workerColumn) && (
                  <div className={rowGridClass}>
                    {assistantColumn}
                    {workerColumn}
                  </div>
                )}
                <SiteAssignmentTimestampHistory sections={historySections} />
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function CompanyNotesSection({ companyId, onSaved, onError }) {
  const [notes, setNotes] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(apiUrl(`/api/admin/companies/${companyId}/notes`), {
        headers: adminHeadersJson(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        onError?.(data.detail || 'Failed to load notes');
        return;
      }
      setNotes(data.notes || []);
    } catch {
      onError?.(apiUnreachableMessage());
    } finally {
      setInitialLoading(false);
    }
  }, [companyId, onError]);

  useEffect(() => {
    setDraft('');
    setInitialLoading(true);
    load();
    return () => {
      setDraft('');
    };
    // Only reload when switching companies, not when parent callbacks re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const saveDraft = async () => {
    const text = draft.trim();
    if (!text || saving) return;

    setSaving(true);
    try {
      const res = await fetch(apiUrl(`/api/admin/companies/${companyId}/notes`), {
        method: 'POST',
        headers: adminHeadersJson(),
        body: JSON.stringify({ note_text: text }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        onError?.(data.detail || 'Failed to add note');
        return;
      }
      setDraft('');
      if (data.note) {
        setNotes((prev) => [data.note, ...prev.filter((note) => note.id !== data.note.id)]);
      } else {
        setNotes((prev) => [
          { id: data.id, note_text: text, created_at: new Date().toISOString(), company_id: companyId },
          ...prev,
        ]);
      }
      onSaved?.(data.message || 'Note added');
    } catch {
      onError?.(apiUnreachableMessage());
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = () => {
    saveDraft();
  };

  const handleDelete = async (noteId) => {
    if (deletingId) return;
    setDeletingId(noteId);
    try {
      const res = await fetch(apiUrl(`/api/admin/companies/${companyId}/notes/${noteId}`), {
        method: 'DELETE',
        headers: adminHeadersJson(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        onError?.(data.detail || 'Failed to delete note');
        return;
      }
      setNotes((prev) => prev.filter((note) => note.id !== noteId));
      onSaved?.('Note removed');
    } catch {
      onError?.(apiUnreachableMessage());
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="px-3 py-3 bg-gray-900/50 sm:px-4 sm:py-4">
      {initialLoading ? (
        <p className="text-gray-500 text-sm">Loading notes...</p>
      ) : (
        <>
          <div className="flex flex-col items-stretch gap-2 mb-4 lg:flex-row lg:items-start lg:gap-3">
            <div className="w-full min-w-0 lg:w-[70%] lg:shrink-0">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={2}
                placeholder="Add a note..."
                className={notesFieldClass}
              />
            </div>
            <div className="flex w-full items-center gap-1.5 lg:w-auto lg:shrink-0 lg:pt-0.5">
              <AddFieldButton
                onClick={handleAddNote}
                title="Add note below"
                disabled={saving || !draft.trim()}
              />
              <button
                type="button"
                onClick={saveDraft}
                disabled={saving || !draft.trim()}
                className="h-8 px-3 rounded border border-[#eda448]/60 text-xs font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-50 whitespace-nowrap"
                title="Save note"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
          {notes.length > 0 && (
            <div className={notes.length > 2 ? 'max-h-[11rem] overflow-y-auto pr-1' : ''}>
              <ul className="space-y-2">
                {notes.map((note) => (
                  <li key={note.id} className="flex flex-col gap-2 lg:flex-row lg:items-start lg:gap-3">
                    <div className="w-full rounded-lg border border-gray-800 bg-black/40 px-3 py-2 lg:w-[70%] lg:shrink-0">
                    {(note.created_at || note.date_time) && (
                      <p className="text-xs text-gray-500 mb-1">
                        {formatNoteTimestamp(note.created_at || note.date_time)}
                      </p>
                    )}
                    <p className="text-sm text-gray-200 whitespace-pre-wrap">{note.text || note.note_text}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 lg:pt-1">
                      <DeleteButton
                        onClick={() => handleDelete(note.id)}
                        deleting={deletingId === note.id}
                        title="Delete note"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function PlaceholderPanel({ title, description, onBack }) {
  return (
    <ListShell title={title} description={description} onBack={onBack}>
      <div className="border border-dashed border-gray-700 rounded-lg p-10 text-center text-gray-500 text-sm">
        This section is planned for a future update. Check back soon.
      </div>
    </ListShell>
  );
}

function UserEditModal({ user, onClose, onSaved, internalOnly = false }) {
  const actor = getStoredUser();
  const isSuperAdminActor = isSuperAdmin(actor?.role);
  const isSuperUserActor = isSuperUser(actor?.role);
  const isCompanyAdminActor = actor?.role === 'admin';
  const canEditRole = isSuperAdminActor || isSuperUserActor || isCompanyAdminActor;
  const roleOptions = roleOptionsForActor(actor, { internalOnly, currentRole: user.role });
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    project_name: user.project_name || '',
    email: user.email || '',
    role: user.role || 'user',
    company_id: user.company_id ? String(user.company_id) : '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const internalCompanyName = DEFAULT_SUPER_ADMIN_COMPANY;

  useEffect(() => {
    if (!isSuperAdminActor && !internalOnly) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/admin/companies'), { headers: adminHeadersJson() });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) setCompanies(data.companies || []);
      } catch {
        if (!cancelled) setCompanies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdminActor, internalOnly]);

  useEffect(() => {
    if (!internalOnly) return;
    const tocId = findCompanyIdByName(companies);
    if (tocId) {
      setForm((prev) => (prev.company_id === tocId ? prev : { ...prev, company_id: tocId }));
    }
  }, [internalOnly, companies]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const body = {
      project_name: form.project_name,
      email: form.email,
      role: form.role,
    };
    if (internalOnly) {
      const tocId = findCompanyIdByName(companies) || form.company_id;
      if (!tocId) {
        setError('Internal company (TOCG) is not configured');
        return;
      }
      body.company_id = Number(tocId);
    } else if (isSuperAdminActor && form.company_id) {
      body.company_id = Number(form.company_id);
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/admin/users/${user.id}`), {
        method: 'PUT',
        headers: adminHeadersJson(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to update user');
        return;
      }
      onSaved(data.message || 'User updated successfully');
      onClose();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <div className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-950 p-6 shadow-xl">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-white mb-1">Edit User</h3>
              <p className="text-gray-400 text-sm truncate">{user.email}</p>
            </div>
            <button
              type="button"
              onClick={() => setShowResetPassword(true)}
              className="shrink-0 px-2.5 py-0.5 rounded bg-sky-300 text-sky-950 text-xs font-semibold hover:bg-sky-200 transition-colors"
            >
              Reset Password
            </button>
          </div>
          {error && (
            <div className="text-red-400 text-sm bg-red-400/10 py-2 px-3 rounded mb-4">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelClass}>User Name</label>
              <input
                name="project_name"
                value={form.project_name}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Email Address</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Role</label>
              {canEditRole ? (
                <select name="role" value={form.role} onChange={handleChange} required className={inputClass}>
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={ROLE_LABELS[user.role] || user.role}
                  disabled
                  className={`${inputClass} opacity-70 cursor-not-allowed`}
                />
              )}
            </div>
          {isSuperAdminActor && !internalOnly ? (
            <div>
              <label className={labelClass}>Company</label>
              <select
                name="company_id"
                value={form.company_id}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="">No company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {internalOnly ? (
            <div>
              <label className={labelClass}>Company</label>
              <input
                type="text"
                value={internalCompanyName}
                disabled
                className={`${inputClass} opacity-70 cursor-not-allowed`}
              />
              <p className="text-xs text-gray-500 mt-1">Internal users are always linked to TOCG.</p>
            </div>
          ) : null}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-1.5 rounded-lg text-sm border border-gray-600 text-gray-300 hover:bg-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-1.5 rounded-lg text-sm font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
      {showResetPassword && (
        <UserResetPasswordModal
          user={user}
          onClose={() => setShowResetPassword(false)}
          onSaved={(message) => {
            setShowResetPassword(false);
            onSaved(message);
          }}
        />
      )}
    </>
  );
}

function UserResetPasswordModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/admin/users/${user.id}`), {
        method: 'PUT',
        headers: adminHeadersJson(),
        body: JSON.stringify({ password: form.password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to reset password');
        return;
      }
      onSaved(data.message || 'Password reset successfully');
      onClose();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-950 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-white mb-1">Reset Password</h3>
        <p className="text-gray-400 text-sm mb-4">{user.email}</p>
        {error && (
          <div className="text-red-400 text-sm bg-red-400/10 py-2 px-3 rounded mb-4">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>New Password</label>
            <PasswordInput
              name="password"
              value={form.password}
              onChange={handleChange}
              required
              minLength={8}
              className={inputClass}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className={labelClass}>Confirm New Password</label>
            <PasswordInput
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              required
              minLength={8}
              className={inputClass}
              autoComplete="new-password"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-1.5 rounded-lg text-sm border border-gray-600 text-gray-300 hover:bg-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-1.5 rounded-lg text-sm font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
            >
              {loading ? 'Saving...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ManageUsersPanel({
  onBack,
  title = 'View / Manage Users',
  description,
  reloadKey = 0,
  showHeaderFilters = false,
  titleAction = null,
}) {
  const actor = getStoredUser();
  const isSuperAdminActor = isSuperAdmin(actor?.role);
  const isSuperUserActor = isSuperUser(actor?.role);
  const roleLabels = isSuperAdminActor ? ROLE_LABELS : ADMIN_VISIBLE_ROLE_LABELS;
  const countCardGridClass =
    isSuperAdminActor || isSuperUserActor
      ? 'grid grid-cols-2 gap-2 mb-3 sm:grid-cols-3'
      : 'grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 w-full';
  const countCardClass =
    isSuperAdminActor || isSuperUserActor
      ? 'rounded-md border border-gray-800 bg-gray-900/80 px-2 py-1.5 text-center'
      : 'rounded-md border border-gray-800 bg-gray-900/80 px-2 py-1.5 text-center min-w-0';
  const countCardValueClass =
    isSuperAdminActor || isSuperUserActor
      ? 'text-lg font-bold text-amber-200/90 leading-tight'
      : 'text-lg font-bold text-amber-200/90 leading-tight';
  const countCardLabelClass =
    isSuperAdminActor || isSuperUserActor
      ? 'text-[11px] text-gray-400 mt-0.5 leading-tight'
      : 'text-[11px] text-gray-400 mt-0.5 leading-tight';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [filterRole, setFilterRole] = useState('');
  const [filterUsername, setFilterUsername] = useState('');
  const [showArchive, setShowArchive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = showArchive ? '?archived=true' : '';
      const res = await fetch(apiUrl(`/api/admin/users${query}`), { headers: adminHeadersJson() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to load users');
        return;
      }
      setRows(data.users || []);
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  }, [showArchive]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDelete = async (row) => {
    setError('');
    setSuccess('');
    setDeletingId(row.id);
    const result = await adminDelete('users', row.id);
    setDeletingId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    showSuccess(`${result.message}. Open Archive to restore.`);
    load();
  };

  const handleRestore = async (row) => {
    setError('');
    setSuccess('');
    setRestoringId(row.id);
    const result = await adminRestore('users', row.id);
    setRestoringId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    showSuccess(result.message);
    load();
  };

  const visibleRows = isSuperAdminActor || isSuperUserActor
    ? rows
    : rows.filter(
        (row) =>
          row.role === 'user' ||
          isSafetySupervisorRole(row.role) ||
          (row.email === actor?.email && row.role === 'admin')
      );

  const roleFilterOptions = useMemo(() => {
    if (isSuperAdminActor) {
      return Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));
    }
    if (isSuperUserActor) {
      return SAFETY_SUPERVISOR_ROLE_OPTIONS;
    }
    return Object.entries(roleLabels).map(([value, label]) => ({ value, label }));
  }, [isSuperAdminActor, isSuperUserActor, roleLabels]);

  const usernameFilterOptions = useMemo(() => {
    const names = new Set();
    visibleRows.forEach((row) => {
      const name = (row.project_name || row.profile_name || '').trim();
      if (name) names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [visibleRows]);

  const filteredRows = useMemo(() => {
    const selectedName = filterUsername.trim().toLowerCase();
    return visibleRows.filter((row) => {
      if (filterRole && (row.role || 'user') !== filterRole) {
        return false;
      }
      if (selectedName) {
        const name = String(row.project_name || row.profile_name || '')
          .trim()
          .toLowerCase();
        if (name !== selectedName) return false;
      }
      return true;
    });
  }, [visibleRows, filterRole, filterUsername]);

  const pagination = usePagination(filteredRows, 10);

  const counts = filteredRows.reduce((acc, u) => {
    const r = u.role || 'user';
    acc[r] = (acc[r] || 0) + 1;
    return acc;
  }, {});

  const hasActiveFilters = Boolean(filterRole || filterUsername.trim());
  const filterSelectClass =
    'px-2.5 py-1.5 bg-black border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-gray-600 min-w-[10rem] max-w-[14rem]';

  const tableColumns = isSuperAdminActor || isSuperUserActor
    ? [
        { key: 'email', label: 'Email' },
        { key: 'project_name', label: 'User Name' },
        {
          key: 'role',
          label: 'Role',
          render: (r) => roleLabels[r.role] || ROLE_LABELS[r.role] || r.role,
        },
        { key: 'company_name', label: 'Company' },
      ]
    : [
        { key: 'email', label: 'Email' },
        {
          key: 'project_name',
          label: 'User Name',
          render: (r) =>
            r.email === actor?.email && r.role === 'admin'
              ? `${r.project_name} (you)`
              : r.project_name,
        },
        {
          key: 'role',
          label: 'Role',
          render: (r) => roleLabels[r.role] || ROLE_LABELS[r.role] || r.role,
        },
      ];

  return (
    <ListShell
      title={showArchive ? `${title} — Archive` : title}
      description={
        showArchive
          ? 'Deleted accounts are kept here. Restore to bring them back.'
          : description ??
            (isSuperAdminActor
              ? 'Manage internal platform users.'
              : isSuperUserActor
                ? 'Manage supervisors.'
                : 'Manage users and supervisors.')
      }
      onBack={onBack}
      wide
      titleAction={
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => setShowArchive((v) => !v)}
            className={archiveToggleClass(showArchive)}
          >
            {showArchive ? '← Back to list' : 'Archive'}
          </button>
          {!showArchive ? titleAction : null}
        </div>
      }
      titleAside={
        showHeaderFilters && !loading && !showArchive ? (
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="filter-internal-role" className="sr-only">
              Filter by role
            </label>
            <select
              id="filter-internal-role"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className={filterSelectClass}
              aria-label="Filter by role"
            >
              <option value="">All roles</option>
              {roleFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <label htmlFor="filter-internal-username" className="sr-only">
              Filter by username
            </label>
            <select
              id="filter-internal-username"
              value={filterUsername}
              onChange={(e) => setFilterUsername(e.target.value)}
              className={filterSelectClass}
              aria-label="Filter by username"
            >
              <option value="">All usernames</option>
              {usernameFilterOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setFilterRole('');
                  setFilterUsername('');
                }}
                className="text-[11px] text-amber-200/80 hover:text-amber-200 underline whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
        ) : null
      }
    >
      <Message error={error} success={success} />
      {loading ? (
        <p className="text-gray-500 text-sm">Loading users...</p>
      ) : (
        <>
          {showHeaderFilters && hasActiveFilters && !showArchive && (
            <p className="text-xs text-gray-500 mb-2">
              Showing {filteredRows.length} of {visibleRows.length} user(s)
            </p>
          )}
          {!showArchive ? (
            <div className={countCardGridClass}>
              {(isSuperAdminActor
                ? Object.entries(roleLabels)
                : isSuperUserActor
                  ? SAFETY_SUPERVISOR_ROLE_OPTIONS.map((o) => [o.value, o.label])
                  : [
                      ['admin', 'Admin (you)'],
                      ['user', 'User/Worker'],
                      ['super_visor', 'Supervisors'],
                      ['assistant_super_visor', 'Assistant Supervisors'],
                    ]
              ).map(([value, label]) =>
                showHeaderFilters ? (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFilterRole((prev) => (prev === value ? '' : value))}
                    className={`${countCardClass} transition-colors ${
                      filterRole === value
                        ? 'border-amber-400/60 bg-amber-950/40'
                        : 'hover:border-gray-600'
                    }`}
                  >
                    <p className={countCardValueClass}>{counts[value] || 0}</p>
                    <p className={countCardLabelClass}>{label}</p>
                  </button>
                ) : (
                  <div key={value} className={countCardClass}>
                    <p className={countCardValueClass}>{counts[value] || 0}</p>
                    <p className={countCardLabelClass}>{label}</p>
                  </div>
                )
              )}
            </div>
          ) : null}
          <AdminDataTable
            columns={
              showArchive
                ? [
                    ...tableColumns,
                    {
                      key: 'deleted_at',
                      label: 'Archived',
                      render: (r) => formatArchivedAt(r.deleted_at),
                    },
                  ]
                : tableColumns
            }
            rows={pagination.pageRows}
            emptyText={
              showArchive
                ? 'Archive is empty.'
                : hasActiveFilters
                  ? 'No users match the selected filters.'
                  : 'No users found.'
            }
            onEdit={showArchive ? undefined : (row) => setEditingUser(row)}
            onDelete={showArchive ? undefined : handleDelete}
            onRestore={showArchive ? handleRestore : undefined}
            deletingId={deletingId}
            restoringId={restoringId}
            canDeleteRow={canDeleteUserRow}
            canEditRow={canEditUserRow}
          />
          <ListPagination {...pagination} />
        </>
      )}
      <button
        type="button"
        onClick={load}
        className="mt-4 text-xs text-amber-200/80 hover:text-amber-200 underline"
      >
        Refresh list
      </button>
      {editingUser && (
        <UserEditModal
          user={editingUser}
          internalOnly={isSuperAdminActor || isSuperUserActor}
          onClose={() => setEditingUser(null)}
          onSaved={(message) => {
            showSuccess(message);
            load();
          }}
        />
      )}
    </ListShell>
  );
}

/** @deprecated Use ManageUsersPanel */
export function ViewUsersPanel({ onBack }) {
  return <ManageUsersPanel onBack={onBack} />;
}

/** @deprecated Use ManageUsersPanel */
export function ManageRolesPanel({ onBack }) {
  return <ManageUsersPanel onBack={onBack} />;
}

function ClientEditModal({ client, companies, onClose, onSaved }) {
  const clientCompanies = companies.filter(
    (company) =>
      company.name?.trim().toUpperCase() !== DEFAULT_SUPER_ADMIN_COMPANY.toUpperCase()
  );
  const [form, setForm] = useState({
    name: client.name || client.project_name || '',
    email: client.email || '',
    role: client.role || 'admin',
    company_id: client.company_id ? String(client.company_id) : '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [companySites, setCompanySites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesOpen, setSitesOpen] = useState(true);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!form.company_id) {
      setCompanySites([]);
      setSelectedSiteIds([]);
      return undefined;
    }
    setSitesLoading(true);
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/admin/sites'), { headers: adminHeadersJson() });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setCompanySites([]);
          return;
        }
        const sites = (data.sites || []).filter(
          (site) => String(site.company_id) === String(form.company_id)
        );
        setCompanySites(sites);
        const userId = client.id || client.user_id;
        const preselected = sites
          .filter(
            (site) =>
              site.assigned_supervisor_user_id === userId ||
              site.assigned_assistant_user_id === userId ||
              site.assigned_to_user_id === userId
          )
          .map((site) => site.id);
        setSelectedSiteIds(preselected);
        setSitesOpen(true);
      } catch {
        if (!cancelled) setCompanySites([]);
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.company_id, client.id, client.user_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'role') {
      setSitesOpen(true);
    }
    if (name === 'company_id') {
      setSelectedSiteIds([]);
      setSitesOpen(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!selectedSiteIds.length) {
      setSitesOpen(true);
      setError('Please assign at least one site');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(apiUrl(`/api/admin/clients/${client.id}`), {
        method: 'PUT',
        headers: adminHeadersJson(),
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          role: form.role,
          company_id: Number(form.company_id),
          site_ids: selectedSiteIds,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to update client');
        return;
      }
      onSaved(data.message || 'Client updated successfully');
      onClose();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <div className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-950 p-6 shadow-xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-white mb-1">Edit Client</h3>
              <p className="text-gray-400 text-sm truncate">{client.email}</p>
            </div>
            <button
              type="button"
              onClick={() => setShowResetPassword(true)}
              className="shrink-0 px-2.5 py-0.5 rounded bg-sky-300 text-sky-950 text-xs font-semibold hover:bg-sky-200 transition-colors"
            >
              Reset Password
            </button>
          </div>
          {error && (
            <div className="text-red-400 text-sm bg-red-400/10 py-2 px-3 rounded mb-4">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelClass}>Client Name</label>
              <input name="name" value={form.name} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Role</label>
              <select name="role" value={form.role} onChange={handleChange} required className={inputClass}>
                {(
                  form.role === 'assistant_super_visor' &&
                  !CLIENT_ROLE_OPTIONS.some((option) => option.value === 'assistant_super_visor')
                    ? [
                        ...CLIENT_ROLE_OPTIONS,
                        { value: 'assistant_super_visor', label: ROLE_LABELS.assistant_super_visor },
                      ]
                    : CLIENT_ROLE_OPTIONS
                ).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {form.role ? (
              <SiteMultiSelectDropdown
                sites={companySites}
                selectedIds={selectedSiteIds}
                onChange={setSelectedSiteIds}
                open={sitesOpen}
                onOpenChange={setSitesOpen}
                loading={sitesLoading}
                disabled={!form.company_id}
                label="Assign sites"
                required
              />
            ) : null}
            <div>
              <label className={labelClass}>Company</label>
              <select name="company_id" value={form.company_id} onChange={handleChange} required className={inputClass}>
                <option value="">Select a company</option>
                {clientCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-1.5 rounded-lg text-sm border border-gray-600 text-gray-300 hover:bg-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-1.5 rounded-lg text-sm font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
      {showResetPassword && (
        <ClientResetPasswordModal
          client={client}
          onClose={() => setShowResetPassword(false)}
          onSaved={(message) => {
            setShowResetPassword(false);
            onSaved(message);
          }}
        />
      )}
    </>
  );
}

function ClientResetPasswordModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/admin/clients/${client.id}`), {
        method: 'PUT',
        headers: adminHeadersJson(),
        body: JSON.stringify({ password: form.password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to reset password');
        return;
      }
      onSaved(data.message || 'Password reset successfully');
      onClose();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-950 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-white mb-1">Reset Client Password</h3>
        <p className="text-gray-400 text-sm mb-4">{client.email}</p>
        {error && (
          <div className="text-red-400 text-sm bg-red-400/10 py-2 px-3 rounded mb-4">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>New Password</label>
            <PasswordInput
              name="password"
              value={form.password}
              onChange={handleChange}
              required
              minLength={8}
              className={inputClass}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className={labelClass}>Confirm New Password</label>
            <PasswordInput
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              required
              minLength={8}
              className={inputClass}
              autoComplete="new-password"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-1.5 rounded-lg text-sm border border-gray-600 text-gray-300 hover:bg-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-1.5 rounded-lg text-sm font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
            >
              {loading ? 'Saving...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CompanyEditModal({ company, onClose, onSaved }) {
  const logoInputRef = useRef(null);
  const [form, setForm] = useState({
    name: company.name || '',
    address: company.address || '',
    contact_email: company.contact_email || '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(
    company.logo_url ? apiUrl(company.logo_url) : null
  );
  const [removeLogo, setRemoveLogo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file for the logo.');
      return;
    }
    setError('');
    setRemoveLogo(false);
    setLogoFile(file);
    setLogoPreview((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append('name', form.name);
      payload.append('address', form.address);
      payload.append('contact_email', form.contact_email);
      if (removeLogo) payload.append('remove_logo', 'true');
      if (logoFile) payload.append('logo', logoFile);

      const res = await fetch(apiUrl(`/api/admin/companies/${company.id}`), {
        method: 'PUT',
        headers: adminHeadersMultipart(),
        body: payload,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to update company');
        return;
      }
      onSaved(data.message || 'Company updated successfully');
      onClose();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/70 p-2 sm:p-4">
      <div className="w-full max-w-md rounded-lg border border-gray-700 bg-gray-950 p-4 shadow-xl max-h-[calc(100dvh-1rem)] overflow-y-auto sm:p-6 sm:max-h-[90vh]">
        <h3 className="text-lg font-semibold text-white mb-1">Edit Company</h3>
        <p className="text-gray-400 text-sm mb-4">{company.name}</p>
        {error && (
          <div className="text-red-400 text-sm bg-red-400/10 py-2 px-3 rounded mb-4">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Company name</label>
            <input name="name" value={form.name} onChange={handleChange} required className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Company logo</label>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoChange}
            />
            {logoPreview && !removeLogo ? (
              <div className="relative w-full h-28 rounded-lg border border-gray-700 bg-gray-900 flex items-center justify-center overflow-hidden mb-2">
                <img src={logoPreview} alt="Logo preview" className="max-h-full max-w-full object-contain" />
                <button
                  type="button"
                  onClick={() => {
                    setRemoveLogo(true);
                    setLogoFile(null);
                    setLogoPreview(null);
                  }}
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/80 text-gray-300 text-xs hover:bg-red-600 hover:text-white"
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className="w-full h-28 rounded-lg border border-dashed border-gray-600 flex items-center justify-center text-sm text-gray-400 hover:border-amber-300 hover:text-amber-200 transition-colors bg-gray-900"
              >
                + Upload logo
              </button>
            )}
            {logoPreview && !removeLogo && (
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className="text-xs text-gray-400 hover:text-white underline"
              >
                Change logo
              </button>
            )}
          </div>
          <div>
            <label className={labelClass}>Address</label>
            <textarea name="address" value={form.address} onChange={handleChange} rows={2} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Contact email</label>
            <input
              type="email"
              name="contact_email"
              value={form.contact_email}
              onChange={handleChange}
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-1.5 rounded-lg text-sm border border-gray-600 text-gray-300 hover:bg-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-1.5 rounded-lg text-sm font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ViewClientsPanel({
  onBack,
  title = 'View / Manage Clients',
  description = 'Manage company admins and users.',
  leadingAction = null,
  titleAction = null,
  reloadKey = 0,
  openCertificateEmployeeId = null,
  openCertificateFocusKey = 0,
  onCertificateFocusHandled = null,
}) {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [certificateClient, setCertificateClient] = useState(null);
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [showArchive, setShowArchive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const clientsQuery = showArchive ? '?archived=true' : '';
      const [clientsRes, companiesRes] = await Promise.all([
        fetch(apiUrl(`/api/admin/clients${clientsQuery}`), { headers: adminHeadersJson() }),
        fetch(apiUrl('/api/admin/companies'), { headers: adminHeadersJson() }),
      ]);
      const clientsData = await clientsRes.json().catch(() => ({}));
      const companiesData = await companiesRes.json().catch(() => ({}));
      if (!clientsRes.ok) {
        setError(clientsData.detail || 'Failed to load clients');
        return;
      }
      setRows(clientsData.clients || []);
      if (companiesRes.ok) {
        setCompanies(companiesData.companies || []);
      }
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  }, [showArchive]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(() => {
    if (!openCertificateEmployeeId || loading) return;
    const match = rows.find(
      (row) => String(row.id) === String(openCertificateEmployeeId)
    );
    if (match) {
      setShowArchive(false);
      setCertificateClient(match);
    }
    onCertificateFocusHandled?.();
  }, [openCertificateEmployeeId, openCertificateFocusKey, loading, rows, onCertificateFocusHandled]);

  const clientCompanies = useMemo(
    () =>
      companies.filter(
        (company) =>
          company.name?.trim().toUpperCase() !== DEFAULT_SUPER_ADMIN_COMPANY.toUpperCase()
      ),
    [companies]
  );

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (filterCompanyId && String(row.company_id) !== String(filterCompanyId)) {
        return false;
      }
      if (filterRole && (row.role || 'user') !== filterRole) {
        return false;
      }
      return true;
    });
  }, [rows, filterCompanyId, filterRole]);

  const pagination = usePagination(filteredRows, 10);

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDelete = async (row) => {
    setError('');
    setSuccess('');
    setDeletingId(row.id);
    const result = await adminDelete('clients', row.id);
    setDeletingId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    showSuccess(`${result.message}. Open Archive to restore.`);
    load();
  };

  const handleRestore = async (row) => {
    setError('');
    setSuccess('');
    setRestoringId(row.id);
    const result = await adminRestore('clients', row.id);
    setRestoringId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    showSuccess(result.message);
    load();
  };

  const counts = filteredRows.reduce((acc, row) => {
    const role = row.role || 'user';
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, {});

  const hasActiveFilters = Boolean(filterCompanyId || filterRole);

  const filterSelectClass =
    'px-2.5 py-1.5 bg-black border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-gray-600 min-w-[10rem] max-w-[14rem]';

  return (
    <ListShell
      title={showArchive ? `${title} — Archive` : title}
      description={
        showArchive
          ? 'Deleted company employees are kept here. Restore to bring them back.'
          : description
      }
      onBack={onBack}
      wide
      leadingAction={leadingAction}
      titleAction={
        <div className="flex w-full items-center gap-2 [&>button]:flex-1 lg:w-auto lg:justify-end lg:[&>button]:flex-none">
          <button
            type="button"
            onClick={() => setShowArchive((v) => !v)}
            className={archiveToggleClass(showArchive)}
          >
            {showArchive ? '← Back to list' : 'Archive'}
          </button>
          {!showArchive ? titleAction : null}
        </div>
      }
      titleAside={
        !loading && !showArchive ? (
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="filter-company" className="sr-only">
              Filter by company
            </label>
            <select
              id="filter-company"
              value={filterCompanyId}
              onChange={(e) => setFilterCompanyId(e.target.value)}
              className={filterSelectClass}
              aria-label="Filter by company"
            >
              <option value="">All companies</option>
              {clientCompanies.map((company) => (
                <option key={company.id} value={String(company.id)}>
                  {company.name}
                </option>
              ))}
            </select>
            <label htmlFor="filter-role" className="sr-only">
              Filter by role
            </label>
            <select
              id="filter-role"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className={filterSelectClass}
              aria-label="Filter by role"
            >
              <option value="">All roles</option>
              {CLIENT_ROLE_STATS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setFilterCompanyId('');
                  setFilterRole('');
                }}
                className="text-[11px] text-amber-200/80 hover:text-amber-200 underline whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
        ) : null
      }
    >
      <Message error={error} success={success} />
      {loading ? (
        <p className="text-gray-500 text-sm">Loading clients...</p>
      ) : (
        <>
          {hasActiveFilters && !showArchive && (
            <p className="text-xs text-gray-500 mb-2">
              Showing {filteredRows.length} of {rows.length} Company Employee(s)
            </p>
          )}
          {!showArchive ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
              {CLIENT_ROLE_STATS_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    setFilterRole((prev) => (prev === option.value ? '' : option.value))
                  }
                  className={`rounded-md border px-2 py-1.5 text-center transition-colors ${
                    filterRole === option.value
                      ? 'border-amber-400/60 bg-amber-950/40'
                      : 'border-gray-800 bg-gray-900/80 hover:border-gray-600'
                  }`}
                >
                  <p className="text-lg font-bold text-amber-200/90 leading-tight">
                    {counts[option.value] || 0}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{option.label}</p>
                </button>
              ))}
            </div>
          ) : null}
          <AdminDataTable
            columns={[
              { key: 'name', label: 'Client Name', render: (r) => r.name || r.project_name },
              { key: 'email', label: 'Email' },
              {
                key: 'role',
                label: 'Role',
                render: (r) => roleLabel(r.role),
              },
              { key: 'company_name', label: 'Company' },
              ...(showArchive
                ? [
                    {
                      key: 'deleted_at',
                      label: 'Archived',
                      render: (r) => formatArchivedAt(r.deleted_at),
                    },
                  ]
                : []),
            ]}
            rows={pagination.pageRows}
            emptyText={
              showArchive
                ? 'Archive is empty.'
                : hasActiveFilters
                  ? 'No Company Employees match the selected filters.'
                  : 'No clients found.'
            }
            onEdit={showArchive ? undefined : (row) => setEditingClient(row)}
            onCertificate={showArchive ? undefined : (row) => setCertificateClient(row)}
            onDelete={showArchive ? undefined : handleDelete}
            onRestore={showArchive ? handleRestore : undefined}
            deletingId={deletingId}
            restoringId={restoringId}
          />
          <ListPagination {...pagination} />
        </>
      )}
      <button type="button" onClick={load} className="mt-4 text-xs text-amber-200/80 hover:text-amber-200 underline">
        Refresh list
      </button>
      {editingClient && (
        <ClientEditModal
          client={editingClient}
          companies={companies}
          onClose={() => setEditingClient(null)}
          onSaved={(message) => {
            showSuccess(message);
            load();
          }}
        />
      )}
      {certificateClient && (
        <CertificateUploadModal
          employee={certificateClient}
          onClose={() => setCertificateClient(null)}
          onSaved={(message) => {
            showSuccess(message);
            load();
          }}
        />
      )}
    </ListShell>
  );
}

export function AddManageClientsPanel({
  onBack,
  createOpen,
  onCreateOpenChange,
  openCertificateEmployeeId = null,
  openCertificateFocusKey = 0,
  onCertificateFocusHandled = null,
}) {
  const [reloadKey, setReloadKey] = useState(0);

  if (createOpen) {
    return (
      <CreateClientForm
        onBack={() => onCreateOpenChange(false)}
        backLabel="← Back to Company Employee list"
        onCreated={() => {
          onCreateOpenChange(false);
          setReloadKey((key) => key + 1);
        }}
      />
    );
  }

  return (
    <ViewClientsPanel
      onBack={onBack}
      reloadKey={reloadKey}
      openCertificateEmployeeId={openCertificateEmployeeId}
      openCertificateFocusKey={openCertificateFocusKey}
      onCertificateFocusHandled={onCertificateFocusHandled}
      title="Add / Manage Company Employees"
      description="Add, edit, or remove Company Employees."
      titleAction={
        <button
          type="button"
          onClick={() => onCreateOpenChange(true)}
          className={`${panelAddButtonClass} w-full min-h-11 lg:w-auto lg:min-h-0`}
        >
          Add Company Employee
        </button>
      }
    />
  );
}

export function ViewCompaniesPanel({
  onBack,
  title = 'View / Manage Company',
  description = 'Edit company details.',
  headerAction = null,
  leadingAction = null,
  titleAction = null,
  reloadKey = 0,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [notesCompanyId, setNotesCompanyId] = useState(null);
  const [showArchive, setShowArchive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = showArchive ? '?archived=true' : '';
      const res = await fetch(apiUrl(`/api/admin/companies${query}`), {
        headers: adminHeadersJson(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to load companies');
        return;
      }
      setRows(data.companies || []);
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  }, [showArchive]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDelete = async (row) => {
    setError('');
    setSuccess('');
    setDeletingId(row.id);
    const result = await adminDelete('companies', row.id);
    setDeletingId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    showSuccess(`${result.message}. Open Archive to restore.`);
    load();
  };

  const handleRestore = async (row) => {
    setError('');
    setSuccess('');
    setRestoringId(row.id);
    const result = await adminRestore('companies', row.id);
    setRestoringId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    showSuccess(result.message);
    load();
  };

  return (
    <ListShell
      title={showArchive ? `${title} — Archive` : title}
      description={
        showArchive
          ? 'Deleted companies are kept here. Restore to bring them back (sites too).'
          : description
      }
      onBack={onBack}
      wide
      headerAction={headerAction}
      leadingAction={leadingAction}
      titleAction={
        <div className="flex w-full items-center gap-2 [&>button]:flex-1 lg:w-auto lg:justify-end lg:[&>button]:flex-none">
          <button
            type="button"
            onClick={() => setShowArchive((v) => !v)}
            className={archiveToggleClass(showArchive)}
          >
            {showArchive ? '← Back to list' : 'Archive'}
          </button>
          {!showArchive ? titleAction : null}
        </div>
      }
    >
      <Message error={error} success={success} />
      {loading ? (
        <p className="text-gray-500 text-sm">Loading companies...</p>
      ) : (
        <AdminDataTable
          columns={[
            {
              key: 'logo_url',
              label: 'Logo',
              render: (r) =>
                r.logo_url ? (
                  <img
                    src={apiUrl(r.logo_url)}
                    alt=""
                    className="h-10 w-10 object-contain rounded bg-gray-900"
                  />
                ) : (
                  <span className="text-gray-600">—</span>
                ),
            },
            { key: 'name', label: 'Company' },
            { key: 'contact_email', label: 'Contact' },
            { key: 'address', label: 'Address' },
            ...(showArchive
              ? [
                  {
                    key: 'deleted_at',
                    label: 'Archived',
                    render: (r) => formatArchivedAt(r.deleted_at),
                  },
                ]
              : []),
          ]}
          rows={rows}
          emptyText={showArchive ? 'Archive is empty.' : 'No companies found.'}
          onNotes={
            showArchive
              ? undefined
              : (row) => setNotesCompanyId((current) => (current === row.id ? null : row.id))
          }
          notesExpandedId={notesCompanyId}
          renderNotesRow={(row) => (
            <CompanyNotesSection
              key={`company-notes-${row.id}`}
              companyId={row.id}
              onSaved={(message) => showSuccess(message)}
              onError={(message) => setError(message)}
            />
          )}
          onEdit={showArchive ? undefined : (row) => setEditingCompany(row)}
          onDelete={showArchive ? undefined : handleDelete}
          onRestore={showArchive ? handleRestore : undefined}
          deletingId={deletingId}
          restoringId={restoringId}
        />
      )}
      <button type="button" onClick={load} className="mt-4 text-xs text-amber-200/80 hover:text-amber-200 underline">
        Refresh list
      </button>
      {editingCompany && (
        <CompanyEditModal
          company={editingCompany}
          onClose={() => setEditingCompany(null)}
          onSaved={(message) => {
            showSuccess(message);
            load();
          }}
        />
      )}
    </ListShell>
  );
}

export function AddManageCompanyPanel({ onBack, createOpen, onCreateOpenChange }) {
  const [reloadKey, setReloadKey] = useState(0);

  if (createOpen) {
    return (
      <AddCompanyForm
        onBack={() => onCreateOpenChange(false)}
        backLabel="← Back to company list"
        onCreated={() => {
          onCreateOpenChange(false);
          setReloadKey((key) => key + 1);
        }}
      />
    );
  }

  return (
    <ViewCompaniesPanel
      onBack={onBack}
      reloadKey={reloadKey}
      title="Add / Manage Company"
      description="Add, edit, or remove companies."
      titleAction={
        <button
          type="button"
          onClick={() => onCreateOpenChange(true)}
          className={`${panelAddButtonClass} w-full min-h-11 lg:w-auto lg:min-h-0`}
        >
          Add Company
        </button>
      }
    />
  );
}

function FormShell({ title, description, onBack, backLabel = '← Back to dashboard', children }) {
  return (
    <div className="w-full max-w-md min-w-0">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-400 hover:text-white mb-4 flex items-center gap-1 break-words text-left"
        >
          {backLabel}
        </button>
      )}
      <h2 className="text-xl font-semibold text-white mb-1">{title}</h2>
      <p className="text-gray-400 text-xs mb-6">{description}</p>
      {children}
    </div>
  );
}

export function CreateClientForm({ onBack, onCreated, backLabel }) {
  const { companies, loading: companiesLoading, error: companiesError, reload } =
    useAdminCompanies();
  const clientCompanies = companies.filter(
    (company) =>
      company.name?.trim().toUpperCase() !== DEFAULT_SUPER_ADMIN_COMPANY.toUpperCase()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [companySites, setCompanySites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesOpen, setSitesOpen] = useState(true);
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [notifyAll, setNotifyAll] = useState(true);
  const certFieldsRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'admin',
    company_id: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (companiesLoading || !clientCompanies.length) return;
    setForm((prev) => {
      if (prev.company_id) return prev;
      const preferred = getPreferredCompanyId(clientCompanies);
      return preferred ? { ...prev, company_id: preferred } : prev;
    });
  }, [clientCompanies, companiesLoading]);

  useEffect(() => {
    let cancelled = false;
    if (!form.company_id) {
      setCompanySites([]);
      return undefined;
    }
    setSitesLoading(true);
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/admin/sites'), { headers: adminHeadersJson() });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setCompanySites([]);
          return;
        }
        const sites = (data.sites || []).filter(
          (site) => String(site.company_id) === String(form.company_id)
        );
        setCompanySites(sites);
      } catch {
        if (!cancelled) setCompanySites([]);
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.company_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'role') {
      setSitesOpen(true);
    }
    if (name === 'company_id') {
      setSelectedSiteIds([]);
      setSitesOpen(Boolean(form.role));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!form.company_id) {
      setError('Please select a company to map this client admin');
      return;
    }
    if (!selectedSiteIds.length) {
      setSitesOpen(true);
      setError('Please assign at least one site');
      return;
    }
    const certResult = certFieldsRef.current?.peekDraft?.() || {};
    if (certResult.error) {
      setError(certResult.error);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/admin/clients'), {
        method: 'POST',
        headers: adminHeadersJson(),
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          role: form.role,
          company_id: Number(form.company_id),
          password: form.password,
          site_ids: selectedSiteIds,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to create client');
        return;
      }
      const userId = data.user_id;
      const certDraft = certResult.draft;
      if (userId && certDraft) {
        try {
          await uploadClientCertificate({
            userId,
            file: certDraft.file,
            files: certDraft.files,
            name: certDraft.name,
            issuedOn: certDraft.issued_on,
            expiresOn: certDraft.expires_on,
            description: certDraft.description,
            notify: notifyAll,
          });
        } catch (certErr) {
          setError(
            `Employee saved, but the certificate could not be uploaded: ${
              certErr?.message || 'upload failed'
            }`
          );
          setForm({
            name: '',
            email: '',
            role: 'admin',
            company_id: getPreferredCompanyId(clientCompanies),
            password: '',
            confirmPassword: '',
          });
          setSelectedSiteIds([]);
          setSitesOpen(true);
          onCreated?.();
          return;
        }
      }
      setSuccess(`Client "${form.name}" added successfully.`);
      setForm({
        name: '',
        email: '',
        role: 'admin',
        company_id: getPreferredCompanyId(clientCompanies),
        password: '',
        confirmPassword: '',
      });
      setSelectedSiteIds([]);
      setSitesOpen(true);
      onCreated?.();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormShell
      title="Create Company Employee"
      description="Add a Company Employee or supervisor."
      onBack={onBack}
      backLabel={backLabel}
    >
      <Message error={error} success={success} />
      <form onSubmit={handleSubmit} className="space-y-4">
        <CompanySelectField
          companies={clientCompanies}
          loading={companiesLoading}
          error={companiesError}
          value={form.company_id}
          onChange={handleChange}
          onRefresh={reload}
          showHints={false}
        />
        <div>
          <label className={labelClass}>Client Name</label>
          <input name="name" value={form.name} onChange={handleChange} required className={inputClass} />
        </div>
        <div>
          <label htmlFor="client_role" className={labelClass}>
            Role
          </label>
          <select
            id="client_role"
            name="role"
            value={form.role}
            onChange={handleChange}
            required
            className={inputClass}
          >
            {CLIENT_ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {form.role ? (
          <SiteMultiSelectDropdown
            sites={companySites}
            selectedIds={selectedSiteIds}
            onChange={setSelectedSiteIds}
            open={sitesOpen}
            onOpenChange={setSitesOpen}
            loading={sitesLoading}
            disabled={!form.company_id}
            label="Assign sites"
            required
          />
        ) : null}
        <div>
          <label className={labelClass}>Email</label>
          <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Password</label>
          <PasswordInput
            name="password"
            value={form.password}
            onChange={handleChange}
            required
            minLength={8}
            className={inputClass}
            autoComplete="new-password"
          />
        </div>
        <div>
          <label className={labelClass}>Confirm Password</label>
          <PasswordInput
            name="confirmPassword"
            value={form.confirmPassword}
            onChange={handleChange}
            required
            minLength={8}
            className={inputClass}
            autoComplete="new-password"
          />
        </div>
        <div className="rounded-lg border border-gray-800 p-3 sm:p-4">
          <div className="mb-3 flex items-start justify-between gap-2">
            <h4 className="text-sm font-semibold text-white">Certificates</h4>
            <CertificatesNotifyRow on={notifyAll} onToggle={setNotifyAll} disabled={loading} />
          </div>
          <EmployeeCertificateFields deferSave fieldsRef={certFieldsRef} notifyOn={notifyAll} />
        </div>
        <button
          type="submit"
          disabled={loading || companiesLoading || !clientCompanies.length}
          className="w-full py-3 rounded-lg font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
        >
          {loading ? 'Saving...' : 'Create Company Employee'}
        </button>
      </form>
    </FormShell>
  );
}

export function AddCompanyForm({ onBack, onCreated, backLabel }) {
  const logoInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', contact_email: '' });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file for the logo.');
      return;
    }
    setError('');
    setLogoFile(file);
    setLogoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const clearLogo = () => {
    setLogoFile(null);
    setLogoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append('name', form.name);
      if (form.address) payload.append('address', form.address);
      if (form.contact_email) payload.append('contact_email', form.contact_email);
      if (logoFile) payload.append('logo', logoFile);

      const res = await fetch(apiUrl('/api/admin/companies'), {
        method: 'POST',
        headers: adminHeadersMultipart(),
        body: payload,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to add company');
        return;
      }
      if (data.id) {
        setLastCreatedCompanyId(data.id);
      }
      setSuccess(
        data.id
          ? `Company "${form.name}" added. Open Add / Manage Company Employees to map an admin to this company.`
          : `Company "${form.name}" added successfully.`
      );
      setForm({ name: '', address: '', contact_email: '' });
      clearLogo();
      onCreated?.();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormShell
      title="Add Company"
      description="Register a company."
      onBack={onBack}
      backLabel={backLabel}
    >
      <Message error={error} success={success} />
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelClass}>Company name</label>
          <input name="name" value={form.name} onChange={handleChange} required className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Company logo</label>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleLogoChange}
          />
          {logoPreview ? (
            <div className="relative w-full h-28 rounded-lg border border-gray-700 bg-gray-900 flex items-center justify-center overflow-hidden mb-2">
              <img src={logoPreview} alt="Logo preview" className="max-h-full max-w-full object-contain" />
              <button
                type="button"
                onClick={clearLogo}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/80 text-gray-300 text-xs hover:bg-red-600 hover:text-white"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => logoInputRef.current?.click()}
              className="w-full h-28 rounded-lg border border-dashed border-gray-600 flex items-center justify-center text-sm text-gray-400 hover:border-amber-300 hover:text-amber-200 transition-colors bg-gray-900"
            >
              + Upload logo
            </button>
          )}
          {logoPreview && (
            <button
              type="button"
              onClick={() => logoInputRef.current?.click()}
              className="text-xs text-gray-400 hover:text-white underline"
            >
              Change logo
            </button>
          )}
        </div>
        <div>
          <label className={labelClass}>Address</label>
          <textarea name="address" value={form.address} onChange={handleChange} rows={2} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Contact email</label>
          <input
            type="email"
            name="contact_email"
            value={form.contact_email}
            onChange={handleChange}
            className={inputClass}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-lg font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
        >
          {loading ? 'Saving...' : 'Add Company'}
        </button>
      </form>
    </FormShell>
  );
}

function SiteEditModal({ site, companies, onClose, onSaved, companyScopeId, companyScopeName }) {
  const [form, setForm] = useState({
    name: site.name || '',
    company_id: companyScopeId
      ? String(companyScopeId)
      : site.company_id
        ? String(site.company_id)
        : '',
    address: site.address || '',
    notes: site.notes || '',
  });
  const [assistantIds, setAssistantIds] = useState(
    () => (site.assistant_super_visors || []).map((assistant) => assistant.id)
  );
  const [allAssistants, setAllAssistants] = useState([]);
  const [assistantsLoading, setAssistantsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/admin/assistant-super-visors'), {
          headers: adminHeadersJson(),
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) {
          setAllAssistants(data.users || []);
        }
      } finally {
        if (!cancelled) setAssistantsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleAssistant = (assistantId) => {
    setAssistantIds((prev) =>
      prev.includes(assistantId)
        ? prev.filter((id) => id !== assistantId)
        : [...prev, assistantId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.company_id) {
      setError('Please select a company');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/admin/sites/${site.id}`), {
        method: 'PUT',
        headers: adminHeadersJson(),
        body: JSON.stringify({
          name: form.name.trim(),
          company_id: Number(form.company_id),
          address: form.address.trim() || null,
          notes: form.notes.trim() || null,
          assistant_super_visor_ids: assistantIds,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to update site / project');
        return;
      }
      onSaved(data.message || 'Site / project updated');
      onClose();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/70 p-2 sm:p-4">
      <div className="w-full max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto rounded-lg border border-gray-700 bg-gray-950 p-4 sm:p-6 lg:max-h-none lg:overflow-visible">
        <h3 className="text-lg font-semibold text-white mb-4">Edit site / project</h3>
        {error && (
          <div className="text-red-400 text-sm bg-red-400/10 py-2 px-3 rounded mb-4">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {companyScopeId ? (
            <div>
              <label className={labelClass}>Company</label>
              <input
                value={companyScopeName || 'Your company'}
                readOnly
                className={`${inputClass} text-gray-400 cursor-not-allowed`}
              />
            </div>
          ) : (
            <CompanySelectField
              companies={companies}
              loading={false}
              value={form.company_id}
              onChange={handleChange}
              showHints={false}
              label="Company"
            />
          )}
          <div>
            <label className={labelClass}>Site / project name</label>
            <input name="name" value={form.name} onChange={handleChange} required className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Address / location</label>
            <textarea name="address" value={form.address} onChange={handleChange} rows={2} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Notes</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Assistant super visors for this site</label>
            {assistantsLoading ? (
              <p className="text-xs text-gray-500">Loading assistant super visors...</p>
            ) : allAssistants.length === 0 ? (
              <p className="text-xs text-gray-500">No assistant super visors available.</p>
            ) : (
              <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-800 bg-black/40 p-3 space-y-2">
                {allAssistants.map((assistant) => (
                  <label key={assistant.id} className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={assistantIds.includes(assistant.id)}
                      onChange={() => toggleAssistant(assistant.id)}
                      className="rounded border-gray-600 bg-black text-amber-400 focus:ring-amber-300/50"
                    />
                    <span>{assistant.name}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Supervisors can assign assistants or workers from the same company.
            </p>
          </div>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] disabled:opacity-70"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ViewSitesPanel({
  onBack,
  title = 'Add / Manage Sites/Projects',
  description = 'Add, edit, or remove sites/projects.',
  reloadKey = 0,
  companyScopeId = null,
  companyScopeName = '',
  readOnly = false,
  allowAssign = false,
  titleAction = null,
}) {
  const actor = getStoredUser();
  const isAssistantViewer = actor?.role === 'assistant_super_visor';
  const showAssignAssistant =
    !isAssistantViewer && (allowAssign || canAssignSites(actor?.role));
  const showAssignSupervisor =
    actor?.role !== 'super_visor' &&
    !isAssistantViewer &&
    (allowAssign || canAssignSites(actor?.role));
  const showAssignProjectManager = showAssignSupervisor;
  const showAssignWorker =
    !readOnly && !isAssistantViewer && canAssignWorkers(actor?.role);
  const { companies, loading: companiesLoading, error: companiesError } = useAdminCompanies({
    enabled: !companyScopeId && !readOnly,
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [editingSite, setEditingSite] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [filterSupervisorId, setFilterSupervisorId] = useState('');
  const [filterSiteId, setFilterSiteId] = useState('');
  const [siteSearch, setSiteSearch] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  const showHeaderFilters =
    (isSuperAdmin(actor?.role) || actor?.role === 'admin') && !readOnly;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = showArchive ? '?archived=true' : '';
      const res = await fetch(apiUrl(`/api/admin/sites${query}`), { headers: adminHeadersJson() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to load sites/projects');
        return;
      }
      setRows(
        sortSitesNewestFirst(data.sites || []).map((site) => ({
          ...site,
          assigned_supervisor_user_id: site.assigned_supervisor_user_id ?? null,
          assigned_supervisor_name: site.assigned_supervisor_name ?? null,
          assigned_supervisor_at: site.assigned_supervisor_at ?? null,
          assigned_assistant_user_id: site.assigned_assistant_user_id ?? null,
          assigned_assistant_name: site.assigned_assistant_name ?? null,
          assigned_assistant_at: site.assigned_assistant_at ?? null,
          assigned_project_manager_user_id: site.assigned_project_manager_user_id ?? null,
          assigned_project_manager_name: site.assigned_project_manager_name ?? null,
          assigned_project_manager_at: site.assigned_project_manager_at ?? null,
          assigned_to_user_id: site.assigned_to_user_id ?? null,
          assigned_to_name: site.assigned_to_name ?? null,
          assigned_at: site.assigned_at ?? null,
          assigned_supervisors: site.assigned_supervisors || [],
          assigned_workers: site.assigned_workers || [],
          assignable_project_managers: site.assignable_project_managers || [],
          supervisor_assignment_history: site.supervisor_assignment_history || [],
          assistant_assignment_history: site.assistant_assignment_history || [],
          worker_assignment_history: site.worker_assignment_history || [],
          project_manager_assignment_history: site.project_manager_assignment_history || [],
        }))
      );
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  }, [showArchive]);

  useEffect(() => {
    setError('');
    setSuccess('');
    setAssigningId(null);
    load();
    return () => {
      setError('');
      setSuccess('');
      setAssigningId(null);
    };
  }, [load, reloadKey]);

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDelete = async (row) => {
    setError('');
    setSuccess('');
    setDeletingId(row.id);
    const result = await adminDelete('sites', row.id);
    setDeletingId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    showSuccess(`${result.message}. Open Archive to restore.`);
    load();
  };

  const handleRestore = async (row) => {
    setError('');
    setSuccess('');
    setRestoringId(row.id);
    const result = await adminRestore('sites', row.id);
    setRestoringId(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    showSuccess(result.message);
    load();
  };

  const handleAssignSupervisor = async (row, supervisorUserIds) => {
    const nextIds = Array.isArray(supervisorUserIds)
      ? supervisorUserIds.map((id) => Number(id)).filter(Boolean)
      : supervisorUserIds
        ? [Number(supervisorUserIds)]
        : [];
    const currentIds = (
      Array.isArray(row.assigned_supervisors) && row.assigned_supervisors.length
        ? row.assigned_supervisors.map((p) => Number(p.id))
        : row.assigned_supervisor_user_id
          ? [Number(row.assigned_supervisor_user_id)]
          : []
    ).filter(Boolean);
    if (
      currentIds.length === nextIds.length &&
      currentIds.every((id, idx) => id === nextIds[idx])
    ) {
      return;
    }

    setError('');
    setSuccess('');
    setAssigningId(row.id);
    try {
      const res = await fetch(apiUrl(`/api/admin/sites/${row.id}/assign-supervisor`), {
        method: 'PATCH',
        headers: adminHeadersJson(),
        body: JSON.stringify({ supervisor_user_ids: nextIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to assign supervisor');
        return;
      }
      const updated = data.site;
      setRows((prev) =>
        prev.map((site) =>
          site.id === row.id
            ? {
                ...site,
                assigned_supervisor_user_id: updated?.assigned_supervisor_user_id ?? null,
                assigned_supervisor_name: updated?.assigned_supervisor_name ?? null,
                assigned_supervisor_at: updated?.assigned_supervisor_at ?? null,
                assigned_supervisors: updated?.assigned_supervisors ?? [],
                supervisor_assignment_history:
                  updated?.supervisor_assignment_history ?? site.supervisor_assignment_history ?? [],
              }
            : site
        )
      );
      showSuccess(data.message || 'Supervisor assignment updated');
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setAssigningId(null);
    }
  };

  const handleAssignAssistantSupervisor = async (row, assistantUserId) => {
    const nextId = assistantUserId ? Number(assistantUserId) : null;
    const currentId =
      row.assigned_assistant_user_id != null && row.assigned_assistant_user_id !== ''
        ? Number(row.assigned_assistant_user_id)
        : null;
    if (currentId === nextId) return;

    setError('');
    setSuccess('');
    setAssigningId(row.id);
    try {
      const res = await fetch(apiUrl(`/api/admin/sites/${row.id}/assign-assistant-supervisor`), {
        method: 'PATCH',
        headers: adminHeadersJson(),
        body: JSON.stringify({ assistant_user_id: nextId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to assign assistant supervisor');
        return;
      }
      const updated = data.site;
      setRows((prev) =>
        prev.map((site) =>
          site.id === row.id
            ? {
                ...site,
                assigned_assistant_user_id: updated?.assigned_assistant_user_id ?? null,
                assigned_assistant_name: updated?.assigned_assistant_name ?? null,
                assigned_assistant_at: updated?.assigned_assistant_at ?? null,
                assistant_assignment_history: updated?.assistant_assignment_history ?? site.assistant_assignment_history ?? [],
              }
            : site
        )
      );
      showSuccess(data.message || 'Assistant supervisor assignment updated');
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setAssigningId(null);
    }
  };

  const handleAssignProjectManager = async (row, projectManagerUserId) => {
    const nextId = projectManagerUserId ? Number(projectManagerUserId) : null;
    const currentId =
      row.assigned_project_manager_user_id != null && row.assigned_project_manager_user_id !== ''
        ? Number(row.assigned_project_manager_user_id)
        : null;
    if (currentId === nextId) return;

    setError('');
    setSuccess('');
    setAssigningId(row.id);
    try {
      const res = await fetch(apiUrl(`/api/admin/sites/${row.id}/assign-project-manager`), {
        method: 'PATCH',
        headers: adminHeadersJson(),
        body: JSON.stringify({ project_manager_user_id: nextId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to assign project manager');
        return;
      }
      const updated = data.site;
      setRows((prev) =>
        prev.map((site) =>
          site.id === row.id
            ? {
                ...site,
                assigned_project_manager_user_id: updated?.assigned_project_manager_user_id ?? null,
                assigned_project_manager_name: updated?.assigned_project_manager_name ?? null,
                assigned_project_manager_at: updated?.assigned_project_manager_at ?? null,
                project_manager_assignment_history:
                  updated?.project_manager_assignment_history ??
                  site.project_manager_assignment_history ??
                  [],
                assignable_project_managers:
                  updated?.assignable_project_managers ?? site.assignable_project_managers ?? [],
              }
            : site
        )
      );
      showSuccess(data.message || 'Project manager assignment updated');
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setAssigningId(null);
    }
  };

  const handleAssignWorker = async (row, workerUserIds) => {
    const nextIds = Array.isArray(workerUserIds)
      ? workerUserIds.map((id) => Number(id)).filter(Boolean)
      : workerUserIds
        ? [Number(workerUserIds)]
        : [];
    const currentIds = (
      Array.isArray(row.assigned_workers) && row.assigned_workers.length
        ? row.assigned_workers.map((p) => Number(p.id))
        : row.assigned_to_user_id
          ? [Number(row.assigned_to_user_id)]
          : []
    ).filter(Boolean);
    if (
      currentIds.length === nextIds.length &&
      currentIds.every((id, idx) => id === nextIds[idx])
    ) {
      return;
    }

    setError('');
    setSuccess('');
    setAssigningId(row.id);
    try {
      const res = await fetch(apiUrl(`/api/admin/sites/${row.id}/assign-worker`), {
        method: 'PATCH',
        headers: adminHeadersJson(),
        body: JSON.stringify({ worker_user_ids: nextIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to assign worker');
        return;
      }
      const updated = data.site;
      setRows((prev) =>
        prev.map((site) =>
          site.id === row.id
            ? {
                ...site,
                assigned_to_user_id: updated?.assigned_to_user_id ?? null,
                assigned_to_name: updated?.assigned_to_name ?? null,
                assigned_at: updated?.assigned_at ?? null,
                assigned_workers: updated?.assigned_workers ?? [],
                worker_assignment_history:
                  updated?.worker_assignment_history ?? site.worker_assignment_history ?? [],
              }
            : site
        )
      );
      showSuccess(data.message || 'Worker assignment updated');
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setAssigningId(null);
    }
  };

  const supervisorOptions = (row) => {
    const byId = new Map();
    (row.assignable_supervisors || []).forEach((person) => {
      byId.set(String(person.id), person);
    });
    (row.assigned_supervisors || []).forEach((person) => {
      if (!byId.has(String(person.id))) {
        byId.set(String(person.id), {
          id: person.id,
          name: person.name || `Supervisor #${person.id}`,
        });
      }
    });
    if (
      row.assigned_supervisor_user_id &&
      !byId.has(String(row.assigned_supervisor_user_id))
    ) {
      byId.set(String(row.assigned_supervisor_user_id), {
        id: row.assigned_supervisor_user_id,
        name: row.assigned_supervisor_name || 'Assigned supervisor',
      });
    }
    return Array.from(byId.values());
  };

  const assistantOptions = (row) => {
    const people = [...(row.assignable_users || [])];
    if (
      row.assigned_assistant_user_id &&
      !people.some((person) => person.id === row.assigned_assistant_user_id)
    ) {
      people.push({
        id: row.assigned_assistant_user_id,
        name: row.assigned_assistant_name || 'Assigned assistant supervisor',
        role: 'assistant_super_visor',
      });
    }
    return people;
  };

  const projectManagerOptions = (row) => {
    const people = [...(row.assignable_project_managers || [])];
    if (
      row.assigned_project_manager_user_id &&
      !people.some((person) => person.id === row.assigned_project_manager_user_id)
    ) {
      people.push({
        id: row.assigned_project_manager_user_id,
        name: row.assigned_project_manager_name || 'Assigned project manager',
        role: 'project_manager',
      });
    }
    return people;
  };

  const workerOptions = (row) => {
    const byId = new Map();
    (row.assignable_users || [])
      .filter((person) => person.role === 'user')
      .forEach((person) => {
        byId.set(String(person.id), person);
      });
    (row.assigned_workers || []).forEach((person) => {
      if (!byId.has(String(person.id))) {
        byId.set(String(person.id), {
          id: person.id,
          name: person.name || `Worker #${person.id}`,
          role: 'user',
        });
      }
    });
    if (row.assigned_to_user_id && !byId.has(String(row.assigned_to_user_id))) {
      byId.set(String(row.assigned_to_user_id), {
        id: row.assigned_to_user_id,
        name: row.assigned_to_name || 'Assigned worker',
        role: 'user',
      });
    }
    return Array.from(byId.values());
  };

  const supervisorFilterOptions = useMemo(() => {
    const byId = new Map();
    rows.forEach((row) => {
      if (row.assigned_supervisor_user_id) {
        byId.set(String(row.assigned_supervisor_user_id), {
          id: row.assigned_supervisor_user_id,
          name: row.assigned_supervisor_name || `Supervisor #${row.assigned_supervisor_user_id}`,
        });
      }
      (row.assigned_supervisors || []).forEach((person) => {
        if (!byId.has(String(person.id))) {
          byId.set(String(person.id), {
            id: person.id,
            name: person.name || `Supervisor #${person.id}`,
          });
        }
      });
      (row.assignable_supervisors || []).forEach((person) => {
        if (!byId.has(String(person.id))) {
          byId.set(String(person.id), { id: person.id, name: person.name });
        }
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name))
    );
  }, [rows]);

  const rowHasSupervisor = (row, supervisorId) => {
    if (!supervisorId) return true;
    if (String(row.assigned_supervisor_user_id || '') === String(supervisorId)) return true;
    return (row.assigned_supervisors || []).some(
      (person) => String(person.id) === String(supervisorId)
    );
  };

  const rowIsUnassignedSupervisor = (row) =>
    !row.assigned_supervisor_user_id && !(row.assigned_supervisors || []).length;

  const filteredRows = useMemo(() => {
    const query = siteSearch.trim().toLowerCase();
    return sortSitesNewestFirst(
      rows.filter((row) => {
        if (filterSupervisorId === '__unassigned__') {
          if (!rowIsUnassignedSupervisor(row)) return false;
        } else if (filterSupervisorId && !rowHasSupervisor(row, filterSupervisorId)) {
          return false;
        }
        if (filterSiteId && String(row.id) !== String(filterSiteId)) {
          return false;
        }
        if (query) {
          const haystack = [row.name, row.address, row.company_name]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(query)) return false;
        }
        return true;
      })
    );
  }, [rows, filterSupervisorId, filterSiteId, siteSearch]);

  const pagination = usePagination(filteredRows, 10);

  const siteFilterOptions = useMemo(() => {
    const base =
      filterSupervisorId === '__unassigned__'
        ? rows.filter((row) => rowIsUnassignedSupervisor(row))
        : filterSupervisorId
          ? rows.filter((row) => rowHasSupervisor(row, filterSupervisorId))
          : rows;
    return sortSitesNewestFirst(base).map((row) => ({ id: row.id, name: row.name }));
  }, [rows, filterSupervisorId]);

  useEffect(() => {
    if (
      filterSiteId &&
      !siteFilterOptions.some((site) => String(site.id) === String(filterSiteId))
    ) {
      setFilterSiteId('');
    }
  }, [filterSiteId, siteFilterOptions]);

  const hasActiveFilters = Boolean(filterSupervisorId || filterSiteId || siteSearch.trim());
  const filterSelectClass =
    'w-[95%] min-w-0 max-w-[22rem] px-2.5 py-2 bg-black border border-gray-700 rounded text-sm text-white focus:outline-none focus:ring-1 focus:ring-gray-600 lg:w-auto lg:min-w-[10rem] lg:max-w-[14rem] lg:py-1.5';
  const searchInputClass =
    'w-full px-2.5 py-2 bg-black border border-gray-700 rounded text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 lg:w-72 lg:py-1.5';

  const siteColumns = [
    { key: 'name', label: 'Site / Project Name' },
    ...(companyScopeId ? [] : [{ key: 'company_name', label: 'Company' }]),
    { key: 'address', label: 'Address / Location' },
    {
      key: 'created_at',
      label: 'Created',
      render: (row) =>
        row.created_at ? (
          <span className="text-xs text-gray-400">{formatNoteTimestamp(row.created_at)}</span>
        ) : (
          '—'
        ),
    },
  ];
  const useAssignmentLayout =
    !showArchive &&
    (showAssignSupervisor ||
      showAssignProjectManager ||
      showAssignAssistant ||
      showAssignWorker);

  return (
    <ListShell
      title={
        showArchive
          ? `${readOnly ? 'Manage Sites/Projects' : title} — Archive`
          : readOnly
            ? 'Manage Sites/Projects'
            : title
      }
      description={
        showArchive
          ? 'Deleted sites/projects are kept here. Restore to bring them back.'
          : useAssignmentLayout
            ? 'Assign Supervisor, then Assistant.'
            : readOnly
              ? 'View sites across companies.'
              : description
      }
      onBack={onBack}
      wide
      titleAction={
        !readOnly ? (
          <div className="flex w-full items-center gap-2 [&>button]:min-w-0 [&>button]:flex-1 lg:w-auto lg:justify-end lg:[&>button]:flex-none">
            <button
              type="button"
              onClick={() => setShowArchive((v) => !v)}
              className={archiveToggleClass(showArchive)}
            >
              {showArchive ? '← Back to list' : 'Archive'}
            </button>
            {!showArchive ? titleAction : null}
          </div>
        ) : (
          titleAction
        )
      }
      titleAside={
        !loading && !showArchive ? (
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:w-auto lg:flex-wrap lg:items-center">
            <label htmlFor="search-sites-assign" className="sr-only">
              Search sites
            </label>
            <input
              id="search-sites-assign"
              type="search"
              value={siteSearch}
              onChange={(e) => setSiteSearch(e.target.value)}
              placeholder="Search sites…"
              className={`${searchInputClass} sm:col-span-2 lg:col-span-1`}
              aria-label="Search sites"
            />
            {showHeaderFilters ? (
              <>
                <label htmlFor="filter-site-supervisor" className="sr-only">
                  Filter by supervisor
                </label>
                <select
                  id="filter-site-supervisor"
                  value={filterSupervisorId}
                  onChange={(e) => setFilterSupervisorId(e.target.value)}
                  className={filterSelectClass}
                  aria-label="Filter by supervisor"
                >
                  <option value="">All supervisors</option>
                  <option value="__unassigned__">Unassigned</option>
                  {supervisorFilterOptions.map((person) => (
                    <option key={person.id} value={String(person.id)}>
                      {person.name}
                    </option>
                  ))}
                </select>
                <label htmlFor="filter-site-name" className="sr-only">
                  Filter by site
                </label>
                <select
                  id="filter-site-name"
                  value={filterSiteId}
                  onChange={(e) => setFilterSiteId(e.target.value)}
                  className={filterSelectClass}
                  aria-label="Filter by site"
                >
                  <option value="">All sites</option>
                  {siteFilterOptions.map((site) => (
                    <option key={site.id} value={String(site.id)}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setFilterSupervisorId('');
                  setFilterSiteId('');
                  setSiteSearch('');
                }}
                className="text-[11px] text-amber-200/80 hover:text-amber-200 underline whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
        ) : null
      }
    >
      <Message error={error} success={success} />
      {loading ? (
        <p className="text-gray-500 text-sm">Loading sites/projects...</p>
      ) : (
        <>
          {hasActiveFilters && !showArchive && (
            <p className="text-xs text-gray-500 mb-4">
              Showing {filteredRows.length} of {rows.length} site(s)
            </p>
          )}
          {useAssignmentLayout ? (
            <SitesAssignmentList
              rows={pagination.pageRows}
              emptyText={
                hasActiveFilters
                  ? 'No sites/projects match the selected filters.'
                  : 'No sites/projects found.'
              }
              companyScopeId={companyScopeId}
              showAssignSupervisor={showAssignSupervisor}
              showAssignProjectManager={showAssignProjectManager}
              showAssignAssistant={showAssignAssistant}
              showAssignWorker={showAssignWorker}
              readOnly={readOnly}
              assigningId={assigningId}
              supervisorOptions={supervisorOptions}
              projectManagerOptions={projectManagerOptions}
              assistantOptions={assistantOptions}
              workerOptions={workerOptions}
              onAssignSupervisor={handleAssignSupervisor}
              onAssignProjectManager={handleAssignProjectManager}
              onAssignAssistantSupervisor={handleAssignAssistantSupervisor}
              onAssignWorker={handleAssignWorker}
              onEdit={readOnly ? undefined : (row) => setEditingSite(row)}
              onDelete={readOnly ? undefined : handleDelete}
              deletingId={deletingId}
            />
          ) : (
            <AdminDataTable
              columns={
                showArchive
                  ? [
                      ...siteColumns,
                      {
                        key: 'deleted_at',
                        label: 'Archived',
                        render: (r) => formatArchivedAt(r.deleted_at),
                      },
                    ]
                  : siteColumns
              }
              rows={pagination.pageRows}
              emptyText={
                showArchive
                  ? 'Archive is empty.'
                  : hasActiveFilters
                    ? 'No sites/projects match the selected filters.'
                    : 'No sites/projects found.'
              }
              onEdit={showArchive || readOnly ? undefined : (row) => setEditingSite(row)}
              onDelete={showArchive || readOnly ? undefined : handleDelete}
              onRestore={showArchive && !readOnly ? handleRestore : undefined}
              deletingId={deletingId}
              restoringId={restoringId}
            />
          )}
          <ListPagination {...pagination} />
        </>
      )}
      <button type="button" onClick={load} className="mt-4 text-xs text-amber-200/80 hover:text-amber-200 underline">
        Refresh list
      </button>
      {editingSite && !readOnly && (
        <SiteEditModal
          site={editingSite}
          companies={companies}
          companyScopeId={companyScopeId}
          companyScopeName={companyScopeName}
          onClose={() => setEditingSite(null)}
          onSaved={(message) => {
            showSuccess(message);
            load();
          }}
        />
      )}
      {companiesError && !companiesLoading && !companyScopeId && !readOnly && (
        <p className="text-xs text-red-400 mt-2">{companiesError}</p>
      )}
    </ListShell>
  );
}

export function AddSiteForm({ onBack, onCreated, backLabel, companyScopeId, companyScopeName }) {
  const { companies, loading: companiesLoading, error: companiesError, reload } =
    useAdminCompanies({ enabled: !companyScopeId });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    name: '',
    company_id: companyScopeId ? String(companyScopeId) : '',
    address: '',
    notes: '',
  });

  useEffect(() => {
    if (companyScopeId || companiesLoading || !companies.length) return;
    setForm((prev) => {
      if (prev.company_id) return prev;
      const preferred = getPreferredCompanyId(companies);
      return preferred ? { ...prev, company_id: preferred } : prev;
    });
  }, [companies, companiesLoading, companyScopeId]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const resolvedCompanyId = companyScopeId || form.company_id;
    if (!resolvedCompanyId) {
      setError('Please select a company for this site / project');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/admin/sites'), {
        method: 'POST',
        headers: adminHeadersJson(),
        body: JSON.stringify({
          name: form.name.trim(),
          company_id: Number(resolvedCompanyId),
          address: form.address.trim() || null,
          notes: form.notes.trim() || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Failed to add site / project');
        return;
      }
      setSuccess(`Site / project "${form.name}" added successfully.`);
      setForm({
        name: '',
        company_id: companyScopeId ? String(companyScopeId) : form.company_id,
        address: '',
        notes: '',
      });
      onCreated?.();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormShell
      title="Add Site / Project"
      description="Add a site/project."
      onBack={onBack}
      backLabel={backLabel}
    >
      <Message error={error} success={success} />
      <form onSubmit={handleSubmit} className="space-y-4">
        {companyScopeId ? (
          <div>
            <label className={labelClass}>Company</label>
            <input
              value={companyScopeName || 'Your company'}
              readOnly
              className={`${inputClass} text-gray-400 cursor-not-allowed`}
            />
          </div>
        ) : (
          <CompanySelectField
            companies={companies}
            loading={companiesLoading}
            error={companiesError}
            value={form.company_id}
            onChange={handleChange}
            onRefresh={reload}
            showHints={false}
            label="Company"
          />
        )}
        <div>
          <label className={labelClass}>Site / project name</label>
          <input name="name" value={form.name} onChange={handleChange} required className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Address / location</label>
          <textarea name="address" value={form.address} onChange={handleChange} rows={2} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Notes</label>
          <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} className={inputClass} />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-lg font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
        >
          {loading ? 'Saving...' : 'Add Site / Project'}
        </button>
      </form>
    </FormShell>
  );
}

export function AddManageSitesPanel({
  onBack,
  createOpen,
  onCreateOpenChange,
  companyScopeId = null,
  companyScopeName = '',
}) {
  const [reloadKey, setReloadKey] = useState(0);

  if (createOpen) {
    return (
      <AddSiteForm
        onBack={() => onCreateOpenChange(false)}
        backLabel="← Back to sites/projects list"
        companyScopeId={companyScopeId}
        companyScopeName={companyScopeName}
        onCreated={() => {
          onCreateOpenChange(false);
          setReloadKey((key) => key + 1);
        }}
      />
    );
  }

  return (
    <ViewSitesPanel
      onBack={onBack}
      reloadKey={reloadKey}
      title="Add / Manage Sites/Projects"
      description="Add, edit, or remove sites/projects."
      companyScopeId={companyScopeId}
      companyScopeName={companyScopeName}
      titleAction={
        <button
          type="button"
          onClick={() => onCreateOpenChange(true)}
          className={`${panelAddButtonClass} w-full min-w-0 min-h-11 truncate lg:w-auto lg:min-h-0`}
        >
          Add Sites/Projects
        </button>
      }
    />
  );
}
